/**
 * SuperAgentDashboard.js
 *
 * Super Agent = independent business operating a physical hub.
 * Their full role:
 *
 * 📥 POKEA (Receive):
 *   A) Walk-in sender → create parcel on the spot (inline form)
 *   B) Online KenteXa order → seller brings order number → confirm receipt
 *   C) Incoming intercity parcel → confirm arrival at hub
 *
 * 📤 TUMA (Dispatch):
 *   A) Send parcel via bus/courier to destination city
 *   B) Assign local agent for last-mile delivery
 *   C) Track what's in transit
 *
 * 💰 MAPATO (Earnings):
 *   A) Cash collected today (manual/offline orders)
 *   B) KenteXa owes me (online order shipping fees pending payout)
 *
 * 📋 BEI (Rate Card):
 *   Quick reference — per kg prices to each city
 *   Answer walk-in questions instantly
 *
 * 🚐 VAN YA LEO (Dar es Salaam only):
 *   Daily batch for same-city deliveries
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import BackBar from '../components/BackBar';
import api from '../../api/api';
import IntelligentLocationInput from '../components/IntelligentLocationInput';
import VerifyIdentityModal from '../components/VerifyIdentityModal';
import HubExpectedShipments from '../components/HubExpectedShipments';
import StaleParcelsNotice from '../components/StaleParcelsNotice';
import { buildReceiptHtml, formatClaimSecret } from '../../utils/receiptPrint';
import { displayParcelNumber, otherParcelNumbers } from '../../utils/counterReceipt';
import { formatWaybill } from '../../utils/waybill';
import { isIntercityPlace, regionRelation } from '../../utils/regionMatch';
import {
  CLAIM_CODE_NOTE, SHOW_CLAIM_CODE_NOTE, saveLastWalkResult, loadLastWalkResult, clearLastWalkResult,
  canHandOverToRecipient, canReopenReceipt, historiaMatches,
} from '../../utils/counterReceipt';
import { needsIdentityStep } from '../../utils/identityState';
import useIdentityRefresh from '../../utils/useIdentityRefresh';

const pendingWalkInRequests = new Map();

// ── Launch scope ──────────────────────────────────────────────────────────
// Full hub-operations dashboard (receive/dispatch/pricing/van) re-enabled —
// the backend endpoints it calls have been live all along.
const HUB_OPS_ENABLED = true;

// ── Constants ─────────────────────────────────────────────────────────────

// CITIES replaced by LocationPicker

const STATUS_LABEL = {
  pending:              { l: 'Inasubiri',        c: '#64748b', bg: '#f1f5f9' },
  received_at_hub:      { l: 'Iko Hubuni',        c: '#1d4ed8', bg: '#eff6ff' },
  verified:             { l: 'Imethibitishwa',    c: '#0891b2', bg: '#ecfeff' },
  ready_for_dispatch:   { l: 'Tayari Kutuma',     c: '#16a34a', bg: '#dcfce7' },
  dispatched:           { l: 'Imetumwa',          c: '#15803d', bg: '#f0fdf4' },
  in_transit:           { l: '🚌 Njiani',         c: '#ca8a04', bg: '#fef9c3' },
  arrived_at_hub:       { l: '📍 Imefika Hubuni', c: '#7c3aed', bg: '#ede9fe' },
  awaiting_buyer:       { l: '⏳ Mteja Achague',  c: '#dc2626', bg: '#fee2e2' },
  out_for_delivery:     { l: '🏍️ Inafikishwa',   c: '#f59e0b', bg: '#fef9c3' },
  delivered:            { l: '✅ Imefikishwa',    c: '#16a34a', bg: '#dcfce7' },
  collection_requested: { l: 'Ombi la Kuchukua',  c: '#f59e0b', bg: '#fef9c3' },
  collected_by_agent:   { l: 'Imechukuliwa',      c: '#7c3aed', bg: '#ede9fe' },
};

const inp = {
  width: '100%', padding: '10px 12px', borderRadius: 8,
  border: '1px solid #e2e8f0', fontSize: 13, boxSizing: 'border-box',
};

const SBadge = ({ status }) => {
  const s = STATUS_LABEL[status] || { l: status, c: '#64748b', bg: '#f1f5f9' };
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px',
      borderRadius: 10, backgroundColor: s.bg, color: s.c }}>
      {s.l}
    </span>
  );
};

// ── Parcel card ────────────────────────────────────────────────────────────

const PCard = ({ p, actions = [] }) => (
  <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: 14,
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)', marginBottom: 10 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between',
      alignItems: 'flex-start', marginBottom: 6 }}>
      <div>
        <div style={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 800,
          color: '#1d4ed8' }}>{displayParcelNumber(p)}</div>
        {otherParcelNumbers(p, formatWaybill).length > 0 && (
          <div style={{ fontSize: 10, fontFamily: 'monospace', color: '#94a3b8' }}>{otherParcelNumbers(p, formatWaybill).join(' · ')}</div>
        )}
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
          {p.originCity} → {p.destinationCity}
          {p.transitCity && <span style={{ color: '#f59e0b' }}> via {p.transitCity}</span>}
        </div>
      </div>
      <SBadge status={p.status} />
    </div>

    {(p.recipientName || p.senderName) && (
      <div style={{ fontSize: 12, color: '#475569', marginBottom: 4 }}>
        {p.senderName && <span>👤 Mtumaji: {p.senderName} </span>}
        {p.recipientName && <span>→ Mpokeaji: {p.recipientName}</span>}
      </div>
    )}

    {/* Phone numbers — previously only visible via the public track page;
        the handling Super Agent needs these directly to actually call
        someone (confirm handover, resolve a delivery issue, etc). */}
    {(p.senderPhone || p.buyerPhone) && (
      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>
        {p.senderPhone && <span>📞 Mtumaji: {p.senderPhone} </span>}
        {p.buyerPhone && <span>📞 Mpokeaji: {p.buyerPhone}</span>}
      </div>
    )}

    {p.description && (
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
        📦 {p.description}
      </div>
    )}

    {/* Payment type badge — COD checked first: a seller_shipment/
        offline_intercity order can ALSO be COD (buyer pays the remainder
        at delivery), which is a completely different instruction from
        "Mtumaji analipa cash" (the sender already paid in full) below. */}
    {p.order?.paymentMethod === 'cod' && !p.order?.codBalanceCollected ? (
      <div style={{ backgroundColor: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 6,
        padding: '3px 8px', fontSize: 10, color: '#1d4ed8', marginBottom: 8, display: 'inline-block' }}>
        🚚 COD — Kusanya TZS {Number(p.order?.codRemainingBalance || 0).toLocaleString()} kwa mpokeaji
      </div>
    ) : p.source === 'online' ? (
      <div style={{ backgroundColor: '#f0fdf4', borderRadius: 6, padding: '3px 8px',
        fontSize: 10, color: '#16a34a', marginBottom: 8, display: 'inline-block' }}>
        💳 KenteXa italipa ada ya usafirishaji
      </div>
    ) : p.source === 'seller_shipment' || p.source === 'offline_intercity' ? (
      <div style={{ backgroundColor: '#eff6ff', borderRadius: 6, padding: '3px 8px',
        fontSize: 10, color: '#1d4ed8', marginBottom: 8, display: 'inline-block' }}>
        💵 Mtumaji analipa cash
      </div>
    ) : null}

    {/* Bus/courier details if dispatched */}
    {p.busTicketNumber && (
      <div style={{ fontSize: 11, color: '#92400e', backgroundColor: '#fef9c3',
        borderRadius: 6, padding: '3px 8px', marginBottom: 8 }}>
        🚌 {p.busCompany} — Tiketi: <strong>{p.busTicketNumber}</strong>
      </div>
    )}

    {actions.length > 0 && (
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
        {actions.map((a, i) => (
          <button key={i} onClick={() => a.fn(p)}
            style={{ padding: '7px 12px', borderRadius: 7, border: 'none',
              cursor: 'pointer', fontSize: 11, fontWeight: 700,
              backgroundColor: a.color || '#1d4ed8', color: '#fff' }}>
            {a.label}
          </button>
        ))}
      </div>
    )}
  </div>
);

// ── Main component ─────────────────────────────────────────────────────────

const SuperAgentDashboard = ({ onNavigate, isLoggedIn, inboxUnread }) => {
  const { t } = useTranslation();

  // ── Core state ────────────────────────────────────────────────────────────
  const [profile, setProfile]           = useState(null);
  const [dashData, setDashData]         = useState(null);
  const [revenue, setRevenue]           = useState(null);
  const [rates, setRates]               = useState([]);
  const [localAgents, setLocalAgents]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [profileStatus, setProfileStatus] = useState(null);
  const [activeTab, setActiveTab]       = useState('pokea');
  const [historiaSearch, setHistoriaSearch] = useState('');
  const [historyModal, setHistoryModal] = useState(null); // { trackingNumber, loading, data, error }
  const [receiptModal, setReceiptModal] = useState(null); // { trackingNumber, loading, data, error }
  const [resendLoading, setResendLoading] = useState(''); // 'sender' | 'receiver' | ''
  const [resendMsg, setResendMsg] = useState('');
  const [error, setError]               = useState('');
  const [identityStatus, setIdentityStatus] = useState(null);
  useIdentityRefresh(setIdentityStatus, true);
  const [showVerifyIdentity, setShowVerifyIdentity] = useState(false);
  const [referralData, setReferralData] = useState(null);
  const [refLinkCopied, setRefLinkCopied] = useState(false);
  const [success, setSuccess]           = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [handoverRequests, setHandoverRequests] = useState([]);
  const [vanIncoming, setVanIncoming] = useState([]);
  const [vanReady, setVanReady] = useState([]);
  const [vanToSend, setVanToSend] = useState([]);
  const [eligibleRuns, setEligibleRuns] = useState({});
  const [selectedRun, setSelectedRun] = useState({});
  const [vanDeskLoading, setVanDeskLoading] = useState(false);
  const fetchVanDesk = async () => {
    try {
      setVanDeskLoading(true);
      const [incomingRes, readyRes, sendRes] = await Promise.all([
        api.get('/van-pilot/desk/blocked-receipts'),
        api.get('/van-pilot/desk/awaiting-completion'),
        api.get('/van-pilot/desk/ready-for-movement'),
      ]);
      setVanIncoming(incomingRes.data || []);
      setVanReady(readyRes.data || []);
      setVanToSend(sendRes.data || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Imeshindwa kupakia shughuli za Van');
    } finally { setVanDeskLoading(false); }
  };

  const loadEligibleRuns = async parcelId => {
    try {
      const res = await api.get(`/van-pilot/parcels/${parcelId}/eligible-runs`);
      setEligibleRuns(p => ({ ...p, [parcelId]: res.data || [] }));
    } catch (err) { setError(err?.response?.data?.message || 'Hakuna Van inayopatikana kwa mzigo huu'); }
  };

  const releaseParcelToVan = async parcel => {
    const run = (eligibleRuns[parcel.id] || []).find(x => Number(x.runId) === Number(selectedRun[parcel.id]));
    if (!run) return;
    try {
      setActionLoading(true); setError('');
      await api.post('/van-pilot/movement-tenders', {
        parcelId: parcel.id,
        transportProviderId: Number(run.transportProviderId),
        runId: Number(run.runId),
        loadRunStopId: Number(run.loadRunStopId),
        idempotencyKey: `hub-release:${parcel.id}:run:${run.runId}:stop:${run.loadRunStopId}`,
      });
      setSuccess('✅ Mzigo umeachiwa kwa transporter. Utatokea kwenye manifest baada ya transporter kuukubali.');
      await fetchVanDesk();
    } catch (err) { setError(err?.response?.data?.message || 'Imeshindwa kuachia mzigo kwa Van'); }
    finally { setActionLoading(false); }
  };

  const confirmVanReceipt = async assignmentId => {
    try {
      setActionLoading(true); setError('');
      await api.patch(`/van-pilot/assignments/${assignmentId}/confirm-receipt`);
      setSuccess('✅ Mzigo umepokelewa na custody imehamia kwenye hub yako.');
      await fetchVanDesk();
      fetchAll();
    } catch (err) {
      setError(err?.response?.data?.message || 'Imeshindwa kuthibitisha mzigo');
    } finally { setActionLoading(false); }
  };

  const acceptHandover = async (id) => {
    try {
      setActionLoading(true); setError('');
      await api.patch(`/collections/${id}/hub-accept`);
      setSuccess('Parcel received at your hub.');
      const requests = await api.get('/collections/hub/handover-requests');
      setHandoverRequests(requests.data || []);
      fetchAll();
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not confirm the handover');
    } finally { setActionLoading(false); }
  };

  // ── POKEA sub-modes ───────────────────────────────────────────────────────
  // 'list' | 'walk_in' | 'online_order' | 'confirm_arrival'
  const [pokeaMode, setPokeaMode]       = useState('list');

  // Walk-in sender form (inline SuperAgentParcel)
  const [walkForm, setWalkForm] = useState({
    senderName: '', senderPhone: '', recipientName: '', recipientPhone: '',
    destinationCity: '', deliveryAddress: '', description: '',
    weightKg: '', declaredValue: '', shippingFeeCollected: '', paymentMethod: 'cash', notes: '',
  });
  const [walkRoute, setWalkRoute]       = useState(null);
  const [walkDestText, setWalkDestText] = useState('');
  const [walkPriceEstimate, setWalkPriceEstimate] = useState(null);
  const [walkHubs, setWalkHubs] = useState([]);
  const [walkHubsLoading, setWalkHubsLoading] = useState(false);
  const [walkDestinationHubId, setWalkDestinationHubId] = useState('');
  const [walkServicePath, setWalkServicePath] = useState('local_agent');
  // C1: 'same' | 'other' | null — set from the office region vs the destination region.
  const [walkRegion, setWalkRegion] = useState(null);
  const [walkPathOverride, setWalkPathOverride] = useState(false);
  const [confirmSending, setConfirmSending]     = useState({});
  const [walkRouteLoading, setWalkRouteLoading] = useState(false);
  const [walkResult, setWalkResult]     = useState(null);

  // Online order receive
  const [onlineOrderId, setOnlineOrderId] = useState('');
  const [orderLookup, setOrderLookup]   = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  // Confirm arrival form
  const [arrivalTracking, setArrivalTracking] = useState('');
  const [arrivalNote, setArrivalNote]   = useState('');

  // ── TUMA state ────────────────────────────────────────────────────────────
  const [dispatchParcel, setDispatchParcel] = useState(null);

  // ── Consolidated shipment (Shehena) state ──────────────────────────────
  // 'open' = list of this agent's open batches (add to one, or start new)
  // 'new'  = pick destination + last-mile agent + first parcels
  // 'add'  = pick more parcels for an already-open batch
  // 'dispatch' = finalize an open batch with transport info
  const [bulkStep, setBulkStep] = useState('open');
  const [bulkOpenShipments, setBulkOpenShipments] = useState([]);
  const [bulkOpenLoading, setBulkOpenLoading] = useState(false);
  const [bulkActiveShipment, setBulkActiveShipment] = useState(null);

  const [bulkDestCity, setBulkDestCity] = useState('');
  // Free-typed city text was silently mismatching real parcel data — a
  // parcel's own destinationCity is set by LocationPicker elsewhere
  // (walk-in, SellerShipment) as districtName ("Arusha City"), not a
  // plainly typed region name ("Arusha"), so a hand-typed guess here
  // never matched and looked like "no parcels for that city" when one
  // genuinely existed. Driven by the same location engine now.
  const [bulkDestText, setBulkDestText] = useState('');
  const [bulkCandidates, setBulkCandidates] = useState([]);
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkHubs, setBulkHubs] = useState([]);
  const [bulkHubsLoading, setBulkHubsLoading] = useState(false);
  const [bulkLastMileAgentId, setBulkLastMileAgentId] = useState(null);
  const [bulkManualContact, setBulkManualContact] = useState({ name: '', phone: '', city: '', address: '' });
  const [bulkTransport, setBulkTransport] = useState({ transportCompany: '', transportRef: '', totalShippingCost: '' });

  const fetchOpenShipments = async () => {
    try {
      setBulkOpenLoading(true);
      const res = await api.get('/super-agents/bulk-shipments/open');
      setBulkOpenShipments(res.data || []);
    } catch { setBulkOpenShipments([]); }
    finally { setBulkOpenLoading(false); }
  };

  const fetchBulkCandidates = async (city) => {
    if (!city) { setBulkCandidates([]); return; }
    try {
      setBulkLoading(true);
      const res = await api.get('/super-agents/bulk-shipments/candidates', { params: { destinationCity: city } });
      setBulkCandidates(res.data || []);
      setBulkSelected(new Set());
    } catch { setBulkCandidates([]); }
    finally { setBulkLoading(false); }
  };

  const fetchBulkHubs = async (city) => {
    if (!city) { setBulkHubs([]); return; }
    try {
      setBulkHubsLoading(true);
      const res = await api.get(`/super-agents/hubs/${encodeURIComponent(city)}`);
      setBulkHubs(res.data || []);
    } catch { setBulkHubs([]); }
    finally { setBulkHubsLoading(false); }
  };

  const resetBulkForm = () => {
    setBulkDestCity(''); setBulkCandidates([]); setBulkSelected(new Set());
    setBulkHubs([]); setBulkLastMileAgentId(null);
    setBulkDestText('');
    setBulkManualContact({ name: '', phone: '', city: '', address: '' });
    setBulkTransport({ transportCompany: '', transportRef: '', totalShippingCost: '' });
    setBulkActiveShipment(null);
  };

  const handleCreateBulkShipment = async () => {
    if (!bulkSelected.size) return;
    if (!bulkLastMileAgentId && !bulkManualContact.name) {
      setError('Chagua Super Agent aliyesajiliwa au jaza mawasiliano ya mshirika'); return;
    }
    try {
      setActionLoading(true); setError('');
      const res = await api.post('/super-agents/bulk-shipments', {
        destinationCity: bulkDestCity,
        destinationSuperAgentId: bulkLastMileAgentId || undefined,
        manualContactName:    bulkLastMileAgentId ? undefined : bulkManualContact.name,
        manualContactPhone:   bulkLastMileAgentId ? undefined : bulkManualContact.phone,
        manualContactCity:    bulkLastMileAgentId ? undefined : bulkManualContact.city,
        manualContactAddress: bulkLastMileAgentId ? undefined : bulkManualContact.address,
        trackingNumbers: [...bulkSelected],
      });
      setSuccess(`✅ Shehena imeundwa — vifurushi ${res.data.linkedCount} vimeongezwa, SMS kwa wanunuzi ${res.data.buyerSmsSentCount}`);
      resetBulkForm();
      setBulkStep('open'); fetchOpenShipments();
    } catch (err) {
      setError(err?.response?.data?.message || 'Imeshindwa kuunda shehena');
    } finally { setActionLoading(false); }
  };

  const handleAddToShipment = async () => {
    if (!bulkSelected.size || !bulkActiveShipment) return;
    try {
      setActionLoading(true); setError('');
      const res = await api.post(`/super-agents/bulk-shipments/${bulkActiveShipment.id}/add-parcels`, {
        trackingNumbers: [...bulkSelected],
      });
      setSuccess(`✅ Vifurushi ${res.data.linkedCount} vimeongezwa kwenye shehena, SMS kwa wanunuzi ${res.data.buyerSmsSentCount}`);
      resetBulkForm();
      setBulkStep('open'); fetchOpenShipments();
    } catch (err) {
      setError(err?.response?.data?.message || 'Imeshindwa kuongeza vifurushi');
    } finally { setActionLoading(false); }
  };

  const handleDispatchBulkShipment = async () => {
    if (!bulkActiveShipment) return;
    try {
      setActionLoading(true); setError('');
      const res = await api.patch(`/super-agents/bulk-shipments/${bulkActiveShipment.id}/dispatch`, {
        transportCompany: bulkTransport.transportCompany || undefined,
        transportRef: bulkTransport.transportRef || undefined,
        totalShippingCost: Number(bulkTransport.totalShippingCost) || undefined,
      });
      setSuccess(`✅ Shehena imetumwa — vifurushi ${res.data.parcelsDispatched}, ujumbe kwa mshirika: ${res.data.agentNotifySent ? 'ndiyo' : 'hapana'}`);
      resetBulkForm();
      setBulkStep('open'); setPokeaMode('list'); fetchAll();
    } catch (err) {
      setError(err?.response?.data?.message || 'Imeshindwa kutuma shehena');
    } finally { setActionLoading(false); }
  };

  // ── Transport Assignment ──────────────────────────────────────────────────
  const [assignTransport,    setAssignTransport]    = useState(null); // { trackingNumber, fromCity, toCity }
  const [transportOptions,   setTransportOptions]   = useState(null); // { published, providers }
  const [loadingTransport,   setLoadingTransport]   = useState(false);
  const [selectedProvider,   setSelectedProvider]   = useState(null);
  const [selectedAvailId,    setSelectedAvailId]    = useState(null);
  const [transportNotes,     setTransportNotes]     = useState('');
  const [agreedPrice,        setAgreedPrice]        = useState('');
  const [assigningTransport, setAssigningTransport] = useState(false);
  const [dispatchForm, setDispatchForm] = useState({
    transportType: 'bus', busCompany: '', busTicketNumber: '',
    busDeparture: '', courierName: '', courierTrackingRef: '', notes: '',
    driverName: '', driverPhone: '', vehicleNumber: '',
    departureDate: '', departureTime: '', expectedArrivalDate: '', expectedArrivalTime: '',
    destinationSuperAgentId: null,
  });
  const [dispatchMode, setDispatchMode] = useState('transport');
  const [selectedAgent, setSelectedAgent] = useState(null);
  // Destination Super Agent hub picker — shown only when dispatching via
  // bus/courier. Optional: the receiving hub sees this parcel in their own
  // "Inakuja" tray and can mark it collected once it physically arrives.
  const [destinationHubs, setDestinationHubs] = useState([]);
  const [loadingHubs, setLoadingHubs] = useState(false);

  // ── Status update ─────────────────────────────────────────────────────────
  const [statusParcel, setStatusParcel] = useState(null);
  const [newStatus, setNewStatus]       = useState('');
  const [statusNote, setStatusNote]     = useState('');
  const [pickupCode, setPickupCode] = useState('');
  const [agentHandoffChallenge, setAgentHandoffChallenge] = useState(null);
  const [codBalanceAmount, setCodBalanceAmount] = useState('');

  const [transferModal, setTransferModal] = useState(null);
  const [transferForm, setTransferForm]   = useState({
    destinationCity: '', destinationSuperAgentId: null,
    manualContactName: '', manualContactPhone: '', manualContactAddress: '',
    transportCompany: '', note: '',
  });
  const [transferHubs, setTransferHubs] = useState([]);
  const [transferHubsLoading, setTransferHubsLoading] = useState(false);
  const [transferDestText, setTransferDestText] = useState('');

  const fetchTransferHubs = async (city) => {
    if (!city) { setTransferHubs([]); return; }
    try {
      setTransferHubsLoading(true);
      const res = await api.get(`/super-agents/hubs/${encodeURIComponent(city)}`);
      setTransferHubs(res.data || []);
    } catch { setTransferHubs([]); }
    finally { setTransferHubsLoading(false); }
  };

  const isDar = profile?.city?.toLowerCase().includes('dar');

  // ── Data loading ──────────────────────────────────────────────────────────

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      api.get('/identity/me').then(r => setIdentityStatus(r.data)).catch(() => {});
      const profileRes = await api.get('/super-agents/my-profile').catch(() => null);
      if (!profileRes?.data || profileRes.data.status === 'not_applied') {
        setProfileStatus('not_applied'); return;
      }
      const p = profileRes.data;
      setProfile(p);
      setProfileStatus(p.status);

      if (p.status === 'active') {
        const [dashRes, revenueRes, ratesRes, agentsRes] = await Promise.all([
          api.get('/super-agents/dashboard'),
          api.get('/super-agents/revenue').catch(() => ({ data: null })),
          api.get(`/super-agents/rates/${encodeURIComponent(p.city)}`).catch(() => ({ data: [] })),
          api.get(`/agents/nearby?region=${encodeURIComponent(p.city)}`).catch(() => ({ data: [] })),
        ]);
        setDashData(dashRes.data);
        setRevenue(revenueRes.data);
        setRates(ratesRes.data || []);
        setLocalAgents(agentsRes.data || []);
        api.get('/collections/hub/handover-requests')
          .then(r => setHandoverRequests(r.data || []))
          .catch(() => setHandoverRequests([]));
        api.get('/super-agents/my-referrals').then(r => setReferralData(r.data)).catch(() => {});
      }
    } catch (err) {
      if (err?.response?.status === 404) setProfileStatus('not_applied');
      else setError(t('super_agent_dashboard.load_failed'));
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => {
    if (!isLoggedIn) { onNavigate('PublicLogin'); return; }
    fetchAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // A walk-in receipt stays reachable after a reload or a wrong tap, until
  // the office presses "Mzigo mpya" (KNOWN_FAULTS #15).
  useEffect(() => {
    if (!profile?.id || walkResult) return;
    const last = loadLastWalkResult(profile.id);
    if (last) setWalkResult(last);
  }, [profile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Parcel grouping ───────────────────────────────────────────────────────

  const parcels       = dashData?.parcels || [];
  const atHub         = parcels.filter(p => ['received_at_hub','verified','ready_for_dispatch'].includes(p.status));
  const incoming      = parcels.filter(p => ['dispatched','in_transit','arrived_at_hub'].includes(p.status) && p.myRole === 'destination');
  // C2: a parcel for another region is never a local-agent parcel, whatever was chosen at intake.
  const isDirectLocalParcel = p => !isIntercityPlace(p.originCity, p.destinationCity) && (p.transportMethod === 'local_agent' || (!p.transportMethod && String(p.originCity || '').trim().toLowerCase() === String(p.destinationCity || '').trim().toLowerCase()));
  const toDispatch    = parcels.filter(p => ['received_at_hub','ready_for_dispatch'].includes(p.status) && p.myRole !== 'destination');
  const inTransit     = parcels.filter(p => ['dispatched','in_transit'].includes(p.status) && p.myRole !== 'destination');
  const awaitingBuyer = parcels.filter(p => ['awaiting_buyer','out_for_delivery'].includes(p.status) && p.myRole !== 'origin');
  // eslint-disable-next-line no-unused-vars
  const delivered     = parcels.filter(p => p.status === 'delivered');

  // Every parcel this hub has ever touched (either role, any status),
  // newest first — previously a delivered/closed-out parcel had no visible
  // list at all, only counted toward a bare summary number.
  const historiaList = [...parcels]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .filter(p => historiaMatches(p, historiaSearch));

  // Earnings
  const cashToday     = parcels.filter(p => {
    const today = new Date().toDateString();
    return p.source !== 'online' && new Date(p.createdAt).toDateString() === today;
  }).reduce((sum, p) => sum + Number(p.shippingFeeCollected || p.actualShippingFee || 0), 0);

  const kenetxaOwes   = parcels.filter(p => p.source === 'online' && !p.agentPaidOut)
    .reduce((sum, p) => sum + Number(p.actualShippingFee || p.superAgentEarnings || 0), 0);

  // ── Walk-in handlers ──────────────────────────────────────────────────────

  const handleSendConfirmLink = async (orderId) => {
    if (!orderId) return;
    setConfirmSending(p => ({ ...p, [orderId]: true }));
    try {
      await api.post(`/orders/${orderId}/send-confirmation`);
      setSuccess('✅ Link ya kuthibitisha imetumwa kwa mnunuzi!');
    } catch { setError('Imeshindwa kutuma link. Jaribu tena.'); }
    finally { setConfirmSending(p => ({ ...p, [orderId]: false })); }
  };

  const lookupWalkRoute = async (dest) => {
    if (!dest || !profile?.city) return;
    try {
      setWalkRouteLoading(true);
      const res = await api.get(
        `/super-agents/route/${encodeURIComponent(profile.city)}/${encodeURIComponent(dest)}`
      );
      setWalkRoute(res.data || null);
    } catch { setWalkRoute(null); }
    finally { setWalkRouteLoading(false); }
  };

  const handleWalkSubmit = async () => {
    if (!walkForm.senderName || !walkForm.recipientName || !walkForm.destinationCity) {
      setError('Jaza sehemu zote zinazohitajika'); return;
    }
    // Thamani ya Mzigo — required, numeric, greater than zero. Matches the
    // same rule the backend now enforces (super-agents.service.ts), so a
    // missing/zero value is caught here with a clear message instead of a
    // raw 400 at submit.
    if (!walkForm.declaredValue || Number(walkForm.declaredValue) <= 0) {
      setError('Weka thamani ya mzigo (lazima iwe zaidi ya sifuri)'); return;
    }
    if (walkServicePath !== 'local_agent' && walkHubs.length > 1 && !walkDestinationHubId) {
      setError('Chagua hub ya mpokeaji'); return;
    }
    try {
      setActionLoading(true); setError('');
      const payload = {
        ...walkForm,
        destinationSuperAgentId: walkServicePath !== 'local_agent' && walkDestinationHubId ? Number(walkDestinationHubId) : undefined,
        servicePath: walkServicePath,
        originCity: profile?.city,
        weightKg:   walkForm.weightKg ? Number(walkForm.weightKg) : undefined,
        declaredValue: Number(walkForm.declaredValue),
        shippingFeeCollected: Number(walkForm.shippingFeeCollected || 0),
      };
      // Reuse the request identity after a lost response or page reload.
      // A changed form starts a different request; the backend also compares
      // every material field before returning an existing receipt.
      const storageKey = `kentexa-walkin-request:${profile?.id}`;
      const fingerprint = JSON.stringify(payload);
      let pending = pendingWalkInRequests.get(storageKey);
      if (!pending) {
        try { pending = JSON.parse(sessionStorage.getItem(storageKey) || 'null'); } catch { pending = null; }
      }
      const requestKey = pending?.fingerprint === fingerprint
        ? pending.requestKey : crypto.randomUUID();
      pendingWalkInRequests.set(storageKey, { fingerprint, requestKey });
      try { sessionStorage.setItem(storageKey, JSON.stringify({ fingerprint, requestKey })); } catch { /* storage unavailable */ }
      const res = await api.post('/super-agents/offline-intercity', { ...payload, requestKey });
      setWalkResult(res.data);
      saveLastWalkResult(profile?.id, res.data);
      pendingWalkInRequests.delete(storageKey);
      try { sessionStorage.removeItem(storageKey); } catch { /* storage unavailable */ }
      // Refresh dashboard data in the background so the new parcel shows up
      // on the Tuma tab immediately — previously required a manual page
      // reload since dashData was only refetched on resetWalk().
      fetchAll();
    } catch (err) {
      setError(err?.response?.data?.message || 'Imeshindwa kuunda agizo');
    } finally { setActionLoading(false); }
  };

  const resetWalk = () => {
    setWalkForm({ senderName: '', senderPhone: '', recipientName: '', recipientPhone: '',
      destinationCity: '', deliveryAddress: '', description: '',
      weightKg: '', declaredValue: '', shippingFeeCollected: '', paymentMethod: 'cash', notes: '' });
    setWalkRoute(null); setWalkResult(null); clearLastWalkResult(profile?.id);
    setWalkHubs([]); setWalkDestinationHubId(''); setWalkServicePath('local_agent'); setWalkRegion(null); setWalkPathOverride(false);
    setPokeaMode('list'); fetchAll();
  };

  // Print a physical receipt for the sender confirming payment received.
  // Their own business name leads as the logistics brand (Super Agents are
  // growing their own brand through every customer touchpoint — same
  // principle already applied to the SMS templates), with Kentexa's
  // verification badge secondary underneath, not the other way around.
  // claimSecret: only from the screen that just created the parcel (first receipt).
  const handlePrintReceipt = (receipt, claimSecret = null) => {
    if (!receipt) return;
    const win = window.open('', '_blank', 'width=380,height=600');
    if (!win) { setError('Imeshindwa kufungua dirisha la kuchapisha'); return; }
    // One universal slip for 58mm, 80mm and A4/Letter (utils/receiptPrint.js).
    win.document.write(buildReceiptHtml(receipt, { claimSecret }));
    win.document.close();
  };

  // ── Online order handlers ─────────────────────────────────────────────────

  const handleLookupOrder = async () => {
    if (!onlineOrderId.trim()) return;
    try {
      setLookupLoading(true); setError('');
      const res = await api.get(`/orders/${onlineOrderId.trim()}/agent-lookup`).catch(() => null)
        || await api.get(`/super-agents/track/${onlineOrderId.trim()}`).catch(() => null);
      setOrderLookup(res?.data || null);
      if (!res?.data) setError('Agizo halipatikani — angalia namba');
    } catch { setError('Imeshindwa kutafuta'); }
    finally { setLookupLoading(false); }
  };

  const handleReceiveOnline = async () => {
    if (!onlineOrderId.trim()) return;
    try {
      setActionLoading(true); setError('');
      // Backend now falls back to the agent's own hub city and the order's
      // known destination if these are omitted, but sending them directly
      // (already visible on-screen from the lookup) avoids relying on that
      // fallback and lets the agent correct the destination if it's wrong.
      await api.patch(`/orders/${onlineOrderId.trim()}/super-agent-receive`, {
        originCity: profile?.city || undefined,
        destinationCity: orderLookup?.destinationCity || orderLookup?.guessedCity || undefined,
      });
      setSuccess(`✅ Agizo #${onlineOrderId} limepokewa hubuni!`);
      setOnlineOrderId(''); setOrderLookup(null);
      setPokeaMode('list'); fetchAll();
    } catch (err) {
      setError(err?.response?.data?.message || 'Imeshindwa kupokea');
    } finally { setActionLoading(false); }
  };

  // ── Confirm arrival handlers ──────────────────────────────────────────────

  const handleConfirmArrival = async () => {
    if (!arrivalTracking.trim()) return;
    try {
      setActionLoading(true); setError('');
      await api.patch(`/super-agents/shipments/${arrivalTracking.trim()}/arrived`, {
        city: profile?.city, note: arrivalNote,
      });
      setSuccess(`✅ ${arrivalTracking} imesajiliwa kufika hubuni`);
      setArrivalTracking(''); setArrivalNote('');
      setPokeaMode('list'); fetchAll();
    } catch (err) {
      setError(err?.response?.data?.message || 'Imeshindwa kusajili');
    } finally { setActionLoading(false); }
  };

  // ── Dispatch handlers ─────────────────────────────────────────────────────


  // ── Transport Assignment functions ────────────────────────────────────────

  const openAssignTransport = async (trackingNumber, fromCity, toCity) => {
    setAssignTransport({ trackingNumber, fromCity, toCity });
    setTransportOptions(null);
    setSelectedProvider(null);
    setSelectedAvailId(null);
    setTransportNotes('');
    setAgreedPrice('');
    try {
      setLoadingTransport(true);
      const res = await api.get(
        `/transport/available?from=${encodeURIComponent(fromCity)}&to=${encodeURIComponent(toCity)}`
      );
      setTransportOptions(res.data);
    } catch { setTransportOptions({ published: [], providers: [] }); }
    finally { setLoadingTransport(false); }
  };

  const handleAssignTransport = async () => {
    if (!selectedProvider) return alert('Chagua msafirishaji kwanza');
    try {
      setAssigningTransport(true);
      await api.post('/transport/assignments', {
        trackingNumber:  assignTransport.trackingNumber,
        providerId:      selectedProvider.id,
        availabilityId:  selectedAvailId || undefined,
        agreedPrice:     agreedPrice ? Number(agreedPrice) : undefined,
        superAgentNotes: transportNotes || undefined,
      });
      alert(`✅ ${selectedProvider.name} wamepewa mgawo!`);
      setAssignTransport(null);
    } catch (e) {
      alert('Imeshindwa: ' + (e.response?.data?.message || 'Jaribu tena'));
    } finally { setAssigningTransport(false); }
  };

  const openParcelHistory = async (trackingNumber) => {
    setHistoryModal({ trackingNumber, loading: true, data: null, error: '' });
    setResendMsg('');
    try {
      const res = await api.get(`/super-agents/track/${trackingNumber}`);
      setHistoryModal({ trackingNumber, loading: false, data: res.data, error: '' });
    } catch (err) {
      setHistoryModal({ trackingNumber, loading: false, data: null,
        error: err?.response?.data?.message || 'Imeshindwa kupata historia' });
    }
  };

  // Handover to the recipient as its own row button (KNOWN_FAULTS #19). Opens
  // the status sheet, whose first block is the pickup-code handover.
  const handoverAction = { label: '🤝 Kabidhi kwa mpokeaji', color: '#16a34a',
    fn: (parcel) => { setStatusParcel(parcel); setNewStatus(''); setPickupCode(''); setCodBalanceAmount(''); } };

  // Re-open a saved walk-in receipt. Read-only: never creates or charges.
  const openReceipt = async (trackingNumber) => {
    setReceiptModal({ trackingNumber, loading: true, data: null, error: '' });
    setResendMsg('');
    try {
      const res = await api.get(`/super-agents/parcels/${encodeURIComponent(trackingNumber)}/receipt`);
      setReceiptModal({ trackingNumber, loading: false, data: res.data, error: '' });
    } catch (err) {
      setReceiptModal({ trackingNumber, loading: false, data: null,
        error: err?.response?.data?.message || 'Imeshindwa kupata risiti' });
    }
  };

  const handleResendSms = async (which, trackingNumber = historyModal?.trackingNumber) => {
    if (!trackingNumber) return;
    try {
      setResendLoading(which);
      setResendMsg('');
      const path = which === 'sender' ? 'resend-sender-sms' : 'resend-receiver-sms';
      const res = await api.post(`/super-agents/parcels/${trackingNumber}/${path}`);
      const sent = which === 'sender' ? res.data?.senderSmsSent : res.data?.receiverSmsSent;
      setResendMsg(sent ? '✅ SMS imetumwa upya' : '⚠️ SMS haikuweza kutumwa');
    } catch (err) {
      setResendMsg('❌ ' + (err?.response?.data?.message || 'Imeshindwa kutuma SMS'));
    } finally {
      setResendLoading('');
    }
  };

  const fetchDestinationHubs = async (city) => {
    if (!city) { setDestinationHubs([]); return; }
    try {
      setLoadingHubs(true);
      const res = await api.get(`/super-agents/hubs/${encodeURIComponent(city)}`);
      setDestinationHubs(res.data || []);
    } catch { setDestinationHubs([]); }
    finally { setLoadingHubs(false); }
  };

  const handleDispatch = async () => {
    if (!dispatchParcel) return;
    try {
      setActionLoading(true); setError('');
      await api.patch(`/super-agents/parcels/${dispatchParcel.trackingNumber}/dispatch`, {
        ...dispatchForm,
        localAgentId: selectedAgent?.id || null,
      });
      setSuccess(`✅ ${displayParcelNumber(dispatchParcel)} imetumwa!`);
      setDispatchParcel(null); setSelectedAgent(null);
      fetchAll();
    } catch (err) {
      setError(err?.response?.data?.message || 'Imeshindwa kutuma');
    } finally { setActionLoading(false); }
  };

  // ── Status update handler ─────────────────────────────────────────────────

  const handleStatus = async () => {
    if (!statusParcel || !newStatus) return;
    try {
      setActionLoading(true); setError('');
      await api.patch(`/super-agents/parcels/${statusParcel.trackingNumber}/status`, {
        status: newStatus, city: profile?.city, note: statusNote,
      });
      setSuccess('✅ Hali imesasishwa');
      setStatusParcel(null); setStatusNote(''); setNewStatus(''); setCodBalanceAmount('');
      fetchAll();
    } catch (err) {
      setError(err?.response?.data?.message || 'Imeshindwa');
    } finally { setActionLoading(false); }
  };

  const handleConfirmPickup = async () => {
    if (!statusParcel || !/^\d{6}$/.test(pickupCode.trim()) ||
        !window.confirm('Je, mpokeaji yupo hapa na ameonyesha namba ya kuthibitisha?')) return;
    try {
      setActionLoading(true); setError('');
      await api.post(`/super-agents/parcels/${statusParcel.trackingNumber}/confirm-pickup`, {
        code: pickupCode.trim(),
        ...(statusParcel.order?.paymentMethod === 'cod'
          ? { codBalanceCollected: Number(codBalanceAmount) } : {}),
      });
      setSuccess('✅ Kifurushi kimekabidhiwa kwa mpokeaji');
      setStatusParcel(null); setStatusNote(''); setNewStatus(''); setPickupCode(''); setCodBalanceAmount('');
      fetchAll();
    } catch (err) {
      setError(err?.response?.data?.message || 'Imeshindwa kuthibitisha makabidhiano');
    } finally { setActionLoading(false); }
  };

  const handleSendPickupCode = async () => {
    if (!statusParcel) return;
    try {
      setActionLoading(true); setError('');
      await api.post(`/super-agents/parcels/${statusParcel.trackingNumber}/pickup-code`);
      setSuccess('Namba ya kuthibitisha imetumwa kwa simu ya mpokeaji');
    } catch (err) {
      setError(err?.response?.data?.message || 'Imeshindwa kutuma namba');
    } finally { setActionLoading(false); }
  };

  const handleIssueAgentHandoff = async () => {
    if (!statusParcel) return;
    try {
      setActionLoading(true); setError(''); setAgentHandoffChallenge(null);
      const { data } = await api.post(`/super-agents/parcels/${statusParcel.trackingNumber}/agent-handoff-code`);
      setAgentHandoffChallenge({ trackingNumber: statusParcel.trackingNumber, code: data.code });
    } catch (err) {
      setError(err?.response?.data?.message || 'Imeshindwa kuandaa makabidhiano ya wakala');
    } finally { setActionLoading(false); }
  };

  // ── Not applied ───────────────────────────────────────────────────────────
  // One office form only (BecomeSuperAgentInfo: ID step, place list, GPS point).

  if (!loading && profileStatus === 'not_applied') return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f1f5f9' }}>
      <BackBar onBack={() => onNavigate('back')} title={t('super_agent_dashboard.apply_title')} top={0} />
      <div style={{ padding: 16, maxWidth: 480, margin: '0 auto', paddingBottom: 32 }}>
        <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: 24,
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginTop: 16 }}>
          <div style={{ fontSize: 36, textAlign: 'center', marginBottom: 12 }}>🏢</div>
          <h2 style={{ textAlign: 'center', fontSize: 18, fontWeight: 900,
            color: '#1e293b', margin: '0 0 6px' }}>{t('super_agent_dashboard.apply_title')}</h2>
          <p style={{ textAlign: 'center', fontSize: 13, color: '#64748b', marginBottom: 20 }}>
            {t('super_agent_dashboard.apply_desc')}
          </p>
          <button onClick={() => onNavigate('BecomeSuperAgentInfo')}
            style={{ width: '100%', backgroundColor: '#1d4ed8', color: '#fff', border: 'none',
              padding: 14, borderRadius: 10, cursor: 'pointer', fontSize: 15, fontWeight: 900 }}>
            {t('become_super_agent_info.form_title')}
          </button>
        </div>
      </div>
    </div>
  );

  if (!loading && profileStatus === 'pending') return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f1f5f9' }}>
      <BackBar onBack={() => onNavigate('back')} title={t('super_agent_dashboard.pending_title')} top={0} />
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
        <h3 style={{ color: '#1e293b' }}>{t('super_agent_dashboard.pending_title')}</h3>
        <p style={{ color: '#64748b', fontSize: 14 }}>{t('super_agent_dashboard.pending_desc')}</p>
      </div>
    </div>
  );

  // ── Launch scope ──────────────────────────────────────────────────────────
  // Registration/application stays fully open (the two early-returns above).
  // The hub operations dashboard (receive/send/pricing/van — everything
  // below this point) stays disabled until that logic gets more real-world
  // testing. Create Shipment is a separate, already-working action and
  // stays available. Flip HUB_OPS_ENABLED back on when ready — nothing
  // below needs to change.
  if (!loading && profileStatus === 'active' && !HUB_OPS_ENABLED) return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#f1f5f9' }}>
      <BackBar onBack={() => onNavigate('back')} title={t('super_agent_dashboard.header_title', 'Hub Dashboard')} top={0} />
      <div style={{ padding: '40px 20px', textAlign: 'center', maxWidth: 420, margin: '0 auto', flex: 1 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🏢</div>
        <h2 style={{ fontSize: 18, fontWeight: 900, color: '#1e293b', margin: '0 0 8px' }}>
          {t('super_agent_dashboard.registered_title', { name: profile?.businessName || t('super_agent_dashboard.your_hub_fallback') })}
        </h2>
        <p style={{ fontSize: 14, color: '#64748b', margin: '0 0 24px', lineHeight: 1.6 }}>
          {t('super_agent_dashboard.registered_desc')}
        </p>
        <button onClick={() => onNavigate('SellerShipment')}
          style={{ background: 'linear-gradient(135deg,#2563EB,#7C3AED)', color: '#fff',
            border: 'none', padding: '14px 28px', borderRadius: 12, cursor: 'pointer',
            fontSize: 14, fontWeight: 800 }}>
          {t('super_agent_dashboard.create_shipment_button')}
        </button>
      </div>
    </div>
  );

  if (loading) return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f1f5f9' }}>
      <BackBar onBack={() => onNavigate('back')} top={0} />
      <div style={{ textAlign: 'center', padding: 80, color: '#94a3b8' }}>{t('super_agent_dashboard.loading')}</div>
    </div>
  );

  // ── Main dashboard ────────────────────────────────────────────────────────

  const handleTransferHub = async (trackingNumber, form) => {
    if (!form.destinationSuperAgentId && !form.manualContactName) {
      alert('Chagua Super Agent aliyesajiliwa au jaza jina la mshirika'); return;
    }
    try {
      const res = await api.post('/super-agents/parcels/' + trackingNumber + '/transfer-hub', {
        destinationSuperAgentId: form.destinationSuperAgentId || undefined,
        manualContactName:    form.destinationSuperAgentId ? undefined : form.manualContactName,
        manualContactPhone:   form.destinationSuperAgentId ? undefined : form.manualContactPhone,
        manualContactCity:    form.destinationSuperAgentId ? undefined : form.destinationCity,
        manualContactAddress: form.destinationSuperAgentId ? undefined : form.manualContactAddress,
        transportCompany: form.transportCompany || undefined,
        note: form.note || undefined,
      });
      alert(`✅ Kimehamishiwa kwa ${res.data.receiverName}. Ujumbe umetumwa kwa mshirika: ${res.data.agentNotifySent ? 'ndiyo' : 'hapana'}, kwa mnunuzi: ${res.data.buyerSmsSent ? 'ndiyo' : 'hapana'}.`);
      setTransferModal(null);
      setTransferForm({ destinationCity: '', destinationSuperAgentId: null, manualContactName: '', manualContactPhone: '', manualContactAddress: '', transportCompany: '', note: '' });
      setTransferDestText('');
      setTransferHubs([]);
      fetchAll();
    } catch (e) {
      alert('Imeshindwa: ' + (e.response?.data?.message || 'Jaribu tena'));
    }
  };


  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column',
      backgroundColor: '#f1f5f9' }}>
      <BackBar onBack={() => onNavigate('back')} title={t('super_agent_dashboard.header_title', 'Hub Dashboard')} top={0} />

      {/* Non-blocking identity prompt — existing active agents from before
          Phase 3 never had to complete Level 1 identity; this never
          restricts anything they can already do, it's purely informational
          (spec: "do not remove existing Super Agents automatically"). */}
      {needsIdentityStep(identityStatus) && (
        <div onClick={() => setShowVerifyIdentity(true)}
          style={{ backgroundColor: '#fef9c3', padding: '10px 16px', fontSize: 12,
            color: '#92400e', fontWeight: 600, cursor: 'pointer', textAlign: 'center' }}>
          🪪 {t('super_agent_dashboard.identity_prompt')}
        </div>
      )}

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div style={{ background: 'linear-gradient(135deg,#0f172a,#1d4ed8)', padding: '16px 16px 0' }}>
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          {/* Hub name + settings */}
          <div style={{ display: 'flex', justifyContent: 'space-between',
            alignItems: 'flex-start', marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 1 }}>
                HUB YA KENTEXA
              </div>
              <div style={{ fontSize: 18, fontWeight: 900, color: '#fff' }}>
                {profile?.businessName || 'Hub Yangu'}
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>
                📍 {profile?.city} · {profile?.agentCode || '—'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => onNavigate('SellerInbox')} title={t('bottom_nav.messages')}
                style={{ position: 'relative', background: 'rgba(255,255,255,0.1)', border: 'none',
                  borderRadius: 8, padding: '8px 12px', cursor: 'pointer',
                  color: '#fff', fontSize: 16 }}>
                💬
                {inboxUnread > 0 && (
                  <span style={{ position: 'absolute', top: -3, right: -3, minWidth: 15, height: 15,
                    padding: '0 3px', borderRadius: 100, backgroundColor: '#DC2626', color: '#fff',
                    fontSize: 9, fontWeight: 800, lineHeight: '15px', textAlign: 'center' }}>
                    {inboxUnread > 99 ? '99+' : inboxUnread}
                  </span>
                )}
              </button>
              <button onClick={() => onNavigate('SuperAgentSettings')}
                style={{ background: 'rgba(255,255,255,0.1)', border: 'none',
                  borderRadius: 8, padding: '8px 12px', cursor: 'pointer',
                  color: '#fff', fontSize: 16 }}>⚙️</button>
            </div>
          </div>

          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 6, marginBottom: 14 }}>
            {[
              { icon: '📥', label: 'Hubuni',  value: atHub.length,         c: '#60a5fa' },
              { icon: '🚌', label: 'Inakuja', value: incoming.length,      c: '#a78bfa' },
              { icon: '📤', label: 'Tuma',    value: toDispatch.length,    c: '#34d399' },
              { icon: '⏳', label: 'Mteja',   value: awaitingBuyer.length, c: '#f87171' },
            ].map(s => (
              <div key={s.label} style={{ backgroundColor: 'rgba(255,255,255,0.08)',
                borderRadius: 10, padding: '8px 4px', textAlign: 'center' }}>
                <div style={{ fontSize: 14 }}>{s.icon}</div>
                <div style={{ fontSize: 20, fontWeight: 900, color: s.c }}>{s.value}</div>
                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)' }}>{s.label}</div>
              </div>
            ))}
          </div>


          {/* Tabs */}
          <div style={{ display: 'flex', gap: 4 }}>
            {[
              { key: 'pokea',   label: '📥 POKEA'   },
              { key: 'tuma',    label: '📤 TUMA'    },
              { key: 'historia',label: '🕘 HISTORIA'},
              { key: 'mapato',  label: '💰 MAPATO'  },
              { key: 'bei',     label: '📋 BEI'     },
              ...(isDar ? [{ key: 'van', label: '🚐 VAN' }] : []),
            ].map(t => (
              <button key={t.key} data-tour={`sa-tab-${t.key}`}
                onClick={() => { setActiveTab(t.key); setPokeaMode('list'); if (t.key === 'van') fetchVanDesk(); }}
                style={{ flex: 1, padding: '9px 4px', border: 'none', cursor: 'pointer',
                  fontSize: 10, fontWeight: 800, borderRadius: '6px 6px 0 0',
                  backgroundColor: activeTab === t.key ? '#f1f5f9' : 'transparent',
                  color: activeTab === t.key ? '#1d4ed8' : 'rgba(255,255,255,0.6)' }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, padding: 16, maxWidth: 560, margin: '0 auto',
        width: '100%', boxSizing: 'border-box', paddingBottom: 80 }}>

        {error && (
          <div style={{ backgroundColor: '#fee2e2', color: '#dc2626',
            padding: '10px 14px', borderRadius: 10, marginBottom: 12, fontSize: 13 }}>
            ❌ {error}
            <button onClick={() => setError('')}
              style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
          </div>
        )}
        {success && (
          <div style={{ backgroundColor: '#dcfce7', color: '#16a34a',
            padding: '10px 14px', borderRadius: 10, marginBottom: 12, fontSize: 13, fontWeight: 700 }}>
            {success}
            <button onClick={() => setSuccess('')}
              style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
          </div>
        )}

        {/* Red notice: received but no dispatch information (separate, never blocks the counter).
            "Add to shehena" opens the existing shehena screen, unchanged. */}
        <StaleParcelsNotice onAddToShehena={() => {
          setActiveTab('pokea'); setPokeaMode('consolidated'); setBulkStep('open'); fetchOpenShipments();
        }} />


        {/* ════════════════════════════════════════════════════════════════
            📥 POKEA TAB
            ════════════════════════════════════════════════════════════════ */}
        {activeTab === 'pokea' && (
          <>
            {/* Sub-mode selector */}
            {pokeaMode === 'list' && (
              <>
                {/* The last walk-in receipt is kept until "Mzigo mpya" — one tap back to it. */}
                {walkResult && (
                  <button onClick={() => setPokeaMode('walk_in')}
                    style={{ width: '100%', marginBottom: 14, padding: '12px 14px', borderRadius: 12,
                      border: '2px solid #1d4ed8', backgroundColor: '#eff6ff', color: '#1d4ed8',
                      cursor: 'pointer', fontSize: 13, fontWeight: 800, textAlign: 'left' }}>
                    🧾 Risiti ya mwisho: <span style={{ fontFamily: 'monospace' }}>{displayParcelNumber(walkResult)}</span> — Fungua
                  </button>
                )}

                {/* Gate 5: booked Shipments this hub is waiting to receive. */}
                <HubExpectedShipments onReceived={fetchAll} />

                {/* Four action buttons */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, marginBottom: 20 }}>
                  <button data-tour="sa-walkin-trigger" onClick={() => setPokeaMode('walk_in')}
                    style={{ backgroundColor: '#16a34a', color: '#fff', border: 'none',
                      padding: '14px 6px', borderRadius: 12, cursor: 'pointer',
                      fontSize: 10, fontWeight: 800, textAlign: 'center' }}>
                    <div style={{ fontSize: 18, marginBottom: 4 }}>👤</div>
                    Walk-In
                  </button>
                  <button onClick={() => setPokeaMode('online_order')}
                    style={{ backgroundColor: '#1d4ed8', color: '#fff', border: 'none',
                      padding: '14px 6px', borderRadius: 12, cursor: 'pointer',
                      fontSize: 10, fontWeight: 800, textAlign: 'center' }}>
                    <div style={{ fontSize: 18, marginBottom: 4 }}>📱</div>
                    Agizo la KenteXa
                  </button>
                  <button onClick={() => setPokeaMode('confirm_arrival')}
                    style={{ backgroundColor: '#7c3aed', color: '#fff', border: 'none',
                      padding: '14px 6px', borderRadius: 12, cursor: 'pointer',
                      fontSize: 10, fontWeight: 800, textAlign: 'center' }}>
                    <div style={{ fontSize: 18, marginBottom: 4 }}>🚌</div>
                    Imefika Hub
                  </button>
                  <button onClick={() => { setPokeaMode('consolidated'); setBulkStep('open'); fetchOpenShipments(); }}
                    style={{ backgroundColor: '#d97706', color: '#fff', border: 'none',
                      padding: '14px 6px', borderRadius: 12, cursor: 'pointer',
                      fontSize: 10, fontWeight: 800, textAlign: 'center' }}>
                    <div style={{ fontSize: 18, marginBottom: 4 }}>📦</div>
                    Shehena
                  </button>
                </div>

                {/* Parcels at hub */}
                {handoverRequests.length > 0 && (
                  <section style={{ marginBottom: 20 }}>
                    <h3 style={{ fontSize: 14 }}>Agent handovers awaiting receipt ({handoverRequests.length})</h3>
                    {handoverRequests.map(job => (
                      <div key={job.id} style={{ background: '#fff', padding: 12, marginBottom: 8, borderRadius: 10 }}>
                        <strong>{job.parcel?.trackingNumber || `Collection #${job.id}`}</strong>
                        <div>{job.agent?.name || 'Agent'} · {job.agent?.phone || ''} · {job.city}</div>
                        <button disabled={actionLoading} onClick={() => acceptHandover(job.id)}>
                          Confirm parcel physically received
                        </button>
                      </div>
                    ))}
                  </section>
                )}
                {atHub.length > 0 && (
                  <>
                    <div style={{ fontSize: 12, fontWeight: 800, color: '#1e293b', marginBottom: 8 }}>
                      🏢 ZIKO HUBUNI — TAYARI KUTUMA ({atHub.length})
                    </div>
                    {atHub.map(p => (
                      <PCard key={p.trackingNumber} p={p} actions={[
                        { label: '📤 Tuma', color: '#16a34a',
                          fn: (parcel) => {
                            setActiveTab('tuma'); setDispatchParcel(parcel);
                            setDispatchForm({ transportType: 'bus', busCompany: '',
                              busTicketNumber: '', busDeparture: '',
                              courierName: '', courierTrackingRef: '', notes: '',
                              driverName: '', driverPhone: '', vehicleNumber: '',
                              departureDate: '', departureTime: '',
                              expectedArrivalDate: '', expectedArrivalTime: '',
                              destinationSuperAgentId: null });
                            setSelectedAgent(null); setDispatchMode('transport');
                            fetchDestinationHubs(parcel.destinationCity);
                          } },
                        { label: 'Sasisha', color: '#64748b',
                          fn: (parcel) => { setStatusParcel(parcel); setNewStatus(''); setPickupCode(''); setCodBalanceAmount(''); } },
                      ]} />
                    ))}
                  </>
                )}

                {/* Incoming intercity parcels */}
                {incoming.length > 0 && (
                  <>
                    <div style={{ fontSize: 12, fontWeight: 800, color: '#7c3aed',
                      marginBottom: 8, marginTop: 16 }}>
                      🚌 ZINAKUJA — KUTOKA MIJI MINGINE ({incoming.length})
                    </div>
                    {incoming.map(p => (
                      <PCard key={p.trackingNumber} p={p} actions={[
                        { label: '✅ Pokea', color: '#7c3aed',
                          fn: (parcel) => { setStatusParcel(parcel); setNewStatus('received_at_hub'); setPickupCode(''); setCodBalanceAmount(''); } },
                        ...(canHandOverToRecipient(p) ? [handoverAction] : []),
                      ]} />
                    ))}
                  </>
                )}

                {/* Awaiting buyer */}
                {awaitingBuyer.length > 0 && (
                  <>
                    <div style={{ fontSize: 12, fontWeight: 800, color: '#dc2626',
                      marginBottom: 8, marginTop: 16 }}>
                      ⏳ ZINASUBIRI MTEJA ({awaitingBuyer.length})
                    </div>
                    {awaitingBuyer.map(p => (
                      <PCard key={p.trackingNumber} p={p} actions={[
                        ...(canHandOverToRecipient(p) ? [handoverAction] : []),
                        { label: 'Sasisha Hali', color: '#7c3aed',
                          fn: (parcel) => { setStatusParcel(parcel); setNewStatus(''); setPickupCode(''); setCodBalanceAmount(''); } },
                      ]} />
                    ))}
                  </>
                )}

                {atHub.length === 0 && incoming.length === 0 && awaitingBuyer.length === 0 && (
                  <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
                    <div style={{ fontSize: 36, marginBottom: 10 }}>📭</div>
                    Hakuna vifurushi vya kupokea sasa
                  </div>
                )}
              </>
            )}

            {/* ── A) WALK-IN FORM ──────────────────────────────────────── */}
            {pokeaMode === 'walk_in' && (
              <div style={{ backgroundColor: '#fff', borderRadius: 14, padding: 18,
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', marginBottom: 16 }}>
                  <div style={{ fontSize: 15, fontWeight: 900, color: '#1e293b' }}>
                    👤 Mteja wa Walk-In
                  </div>
                  {/* On the receipt screen the only way out is "Mzigo mpya". */}
                  {!walkResult && (
                    <button onClick={() => setPokeaMode('list')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer',
                        fontSize: 20, color: '#64748b' }}>×</button>
                  )}
                </div>

                {!walkResult ? (
                  <>
                    {/* Sender */}
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b',
                      marginBottom: 8, letterSpacing: 0.5 }}>MTUMAJI</div>
                    {[
                      { k: 'senderName',  l: 'Jina *',  ph: 'Juma Hamisi' },
                      { k: 'senderPhone', l: 'Simu *',  ph: '0712345678' },
                    ].map(f => (
                      <div key={f.k} style={{ marginBottom: 10 }}>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#475569',
                          display: 'block', marginBottom: 3 }}>{f.l}</label>
                        <input type="text" placeholder={f.ph} value={walkForm[f.k]}
                          onChange={e => setWalkForm(p => ({ ...p, [f.k]: e.target.value }))}
                          style={inp} />
                      </div>
                    ))}

                    {/* Recipient */}
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b',
                      margin: '14px 0 8px', letterSpacing: 0.5 }}>MPOKEAJI</div>
                    {[
                      { k: 'recipientName',  l: 'Jina *',    ph: 'Amina Hassan' },
                      { k: 'recipientPhone', l: 'Simu *',    ph: '0787654321' },
                    ].map(f => (
                      <div key={f.k} style={{ marginBottom: 10 }}>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#475569',
                          display: 'block', marginBottom: 3 }}>{f.l}</label>
                        <input type="text" placeholder={f.ph} value={walkForm[f.k]}
                          onChange={e => setWalkForm(p => ({ ...p, [f.k]: e.target.value }))}
                          style={inp} />
                      </div>
                    ))}

                    <div data-tour="sa-walkin-destination" style={{ marginBottom: 10 }}>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#475569',
                        display: 'block', marginBottom: 3 }}>Mji wa Mwisho *</label>
                      <IntelligentLocationInput
                        label=""
                        value={walkDestText}
                        onTextChange={text => { setWalkDestText(text); setWalkDestinationHubId(''); setWalkHubs([]); setWalkRegion(null); setWalkPathOverride(false); }}
                        onResolved={async loc => {
                          if (!loc) return;
                          const cityStr = loc.regionName || loc.districtName || '';
                          setWalkForm(p => ({ ...p, destinationCity: cityStr, deliveryAddress: loc.displayLabel || p.deliveryAddress }));
                          // C1: another region = intercity, same region = local; the office can still override.
                          const relation = regionRelation(profile?.city, loc.regionName);
                          setWalkRegion(relation); setWalkPathOverride(false);
                          if (relation === 'other') setWalkServicePath('intercity');
                          else if (relation === 'same') setWalkServicePath('local_agent');
                          if (cityStr) {
                            setWalkHubsLoading(true);
                            try {
                              const hubs = await api.get(`/super-agents/hubs/${encodeURIComponent(cityStr)}`);
                              setWalkHubs(hubs.data || []);
                              if (hubs.data?.length === 1) setWalkDestinationHubId(String(hubs.data[0].id));
                            } catch { setWalkHubs([]); }
                            finally { setWalkHubsLoading(false); }
                          }
                          lookupWalkRoute(cityStr);
                          if (cityStr && profile?.city) {
                            try {
                              const params = new URLSearchParams({
                                from: profile.city, to: loc.regionName || cityStr,
                                weight: String(walkForm.weightKg || 1),
                                ...(loc.districtName ? { destDistrict: loc.districtName } : {}),
                              });
                              const res = await api.get(`/pricing/estimate?${params}`);
                              setWalkPriceEstimate(res.data);
                            } catch { setWalkPriceEstimate(null); }
                          }
                        }}
                        placeholder="Andika eneo la mpokeaji, mfano Mbezi Mwisho"
                        required
                      />
                    </div>
                    {/* Execution path is explicit: a hub is not proof that Van is required. */}
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display:'block', fontSize:12, fontWeight:800, marginBottom:7 }}>Mzigo ufikeje?</label>
                      {/* C1: chosen from the office region vs the destination region; all
                          three only when the region is unknown or the office overrides. */}
                      {walkRegion === 'other' && !walkPathOverride && (
                        <div style={{ fontSize:12, color:'#5b21b6', marginBottom:6 }}>Mkoa mwingine — Intercity imechaguliwa</div>
                      )}
                      {(walkRegion !== 'other' || walkPathOverride) && (
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                          <button type="button" onClick={()=>{setWalkServicePath('local_agent');setWalkDestinationHubId('');}}
                            style={{padding:10,borderRadius:9,border:walkServicePath==='local_agent'?'2px solid #16a34a':'1px solid #e2e8f0',background:walkServicePath==='local_agent'?'#f0fdf4':'#fff',fontWeight:800}}>🏍️ Agent delivery</button>
                          <button type="button" onClick={()=>setWalkServicePath('van')}
                            style={{padding:10,borderRadius:9,border:walkServicePath==='van'?'2px solid #1d4ed8':'1px solid #e2e8f0',background:walkServicePath==='van'?'#eff6ff':'#fff',fontWeight:800}}>🚐 Kentexa Van</button>
                        </div>
                      )}
                      {(walkRegion !== 'same' || walkPathOverride) && (
                        <button type="button" onClick={()=>setWalkServicePath('intercity')}
                          style={{width:'100%',marginTop:walkRegion === 'other' && !walkPathOverride ? 0 : 8,padding:9,borderRadius:9,border:walkServicePath==='intercity'?'2px solid #7c3aed':'1px solid #e2e8f0',background:walkServicePath==='intercity'?'#f5f3ff':'#fff',fontWeight:800}}>🚌 Intercity / Courier</button>
                      )}
                      {walkRegion && !walkPathOverride && (
                        <button type="button" onClick={()=>setWalkPathOverride(true)}
                          style={{ marginTop:6, background:'none', border:'none', padding:0, color:'#1d4ed8', fontSize:12, fontWeight:700, cursor:'pointer', textDecoration:'underline' }}>
                          Badilisha kwa mkono
                        </button>
                      )}
                    </div>
                    {walkServicePath !== 'local_agent' && walkHubsLoading && <div>Inatafuta hub za mpokeaji...</div>}
                    {walkServicePath !== 'local_agent' && walkHubs.length > 0 && (
                      <div style={{ marginBottom: 12 }}>
                        <label style={{ display: 'block', fontSize: 14, fontWeight: 700, marginBottom: 6 }}>
                          Hub ya mpokeaji {walkHubs.length > 1 ? '*' : ''}
                        </label>
                        <select value={walkDestinationHubId} onChange={e => setWalkDestinationHubId(e.target.value)} style={inp}>
                          <option value="">{walkHubs.length > 1 ? 'Chagua hub' : 'Hub pekee itachaguliwa'}</option>
                          {walkHubs.map(hub => <option key={hub.id} value={hub.id}>{hub.businessName} · {hub.city}</option>)}
                        </select>
                      </div>
                    )}

                    {/* Route info */}
                    {/* Live price estimate */}
                    {walkPriceEstimate && (
                      <div style={{ borderRadius: 10, padding: '10px 14px', marginBottom: 10,
                        backgroundColor: walkPriceEstimate.confidence === 'exact' ? '#f0fdf4' : '#fff7ed',
                        border: `1px solid ${walkPriceEstimate.confidence === 'exact' ? '#86efac' : '#fed7aa'}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 700,
                              color: walkPriceEstimate.confidence === 'exact' ? '#15803d' : '#92400e' }}>
                              {walkPriceEstimate.confidence === 'exact' ? '✅ Bei Halisi' : '📊 Makisio ya Bei'}
                            </div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>
                              {walkPriceEstimate.displayNote}
                              {walkPriceEstimate.via && ` · ${walkPriceEstimate.via}`}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 18, fontWeight: 900,
                              color: walkPriceEstimate.confidence === 'exact' ? '#16a34a' : '#f59e0b' }}>
                              {walkPriceEstimate.displayPrice}
                            </div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>
                              Siku {walkPriceEstimate.estimatedDays}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {walkRouteLoading && (
                      <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 10 }}>
                        ⏳ Inatafuta njia...
                      </div>
                    )}
                    {walkRoute && !walkRouteLoading && (
                      <div style={{ backgroundColor: '#f0fdf4', borderRadius: 8,
                        padding: '8px 12px', marginBottom: 10, fontSize: 12, color: '#15803d' }}>
                        📍 {profile?.city} → {walkForm.destinationCity}
                        {walkRoute.transitCity && ` via ${walkRoute.transitCity}`}
                        {' '}· Siku {walkRoute.estimatedDays}
                        {walkRoute.baseShippingFee && (
                          <span style={{ marginLeft: 8, fontWeight: 700 }}>
                            · ~TZS {Number(walkRoute.baseShippingFee).toLocaleString()}
                          </span>
                        )}
                      </div>
                    )}

                    <div style={{ marginBottom: 10 }}>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#475569',
                        display: 'block', marginBottom: 3 }}>Anwani ya Mpokeaji *</label>
                      <input type="text" placeholder="Mtaa, alama muhimu"
                        value={walkForm.deliveryAddress}
                        onChange={e => setWalkForm(p => ({ ...p, deliveryAddress: e.target.value }))}
                        style={inp} />
                    </div>

                    {/* Parcel + fee */}
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b',
                      margin: '14px 0 8px', letterSpacing: 0.5 }}>BIDHAA NA MALIPO</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      {[
                        { k: 'description',        l: 'Bidhaa',      ph: 'e.g. Nguo, Simu' },
                        { k: 'weightKg',            l: 'Uzito (kg)',   ph: '2.5', type: 'number' },
                        { k: 'declaredValue',       l: 'Thamani ya Mzigo (TZS) *', ph: '150000', type: 'number' },
                        { k: 'shippingFeeCollected',l: 'Ada Uliyokusanya (TZS)', ph: '8000', type: 'number' },
                      ].map(f => (
                        <div key={f.k} style={{ marginBottom: 8 }}>
                          <label style={{ fontSize: 11, fontWeight: 700, color: '#475569',
                            display: 'block', marginBottom: 3 }}>{f.l}</label>
                          <input type={f.type || 'text'} placeholder={f.ph}
                            value={walkForm[f.k]}
                            onChange={e => setWalkForm(p => ({ ...p, [f.k]: e.target.value }))}
                            style={inp} />
                          {f.k === 'declaredValue' && (
                            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3, lineHeight: 1.4 }}>
                              Weka thamani halisi ya bidhaa zilizomo kwenye mzigo. Thamani hii inaweza kutumika kama kumbukumbu katika kushughulikia upotevu, uharibifu au madai.
                            </div>
                          )}
                        </div>
                      ))}
                      <div style={{ marginBottom: 8 }}>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#475569',
                          display: 'block', marginBottom: 3 }}>Malipo</label>
                        <select value={walkForm.paymentMethod}
                          onChange={e => setWalkForm(p => ({ ...p, paymentMethod: e.target.value }))}
                          style={inp}>
                          <option value="cash">Cash</option>
                          <option value="mpesa">M-Pesa</option>
                          <option value="airtel">Airtel Money</option>
                        </select>
                      </div>
                    </div>

                    <button data-tour="sa-walkin-submit" onClick={handleWalkSubmit} disabled={actionLoading}
                      style={{ width: '100%', backgroundColor: actionLoading ? '#94a3b8' : '#16a34a',
                        color: '#fff', border: 'none', padding: 14, borderRadius: 10,
                        cursor: 'pointer', fontSize: 14, fontWeight: 900, marginTop: 8 }}>
                      {actionLoading ? '⏳ Inasajili...' : '📥 Sajili Kifurushi'}
                    </button>
                  </>
                ) : (
                  /* Success — show tracking number. No separate platform-fee
                     payment step here — Kentexa's platform fee (free for the
                     first 50 orders, then billed per-order to the Super
                     Agent's own account balance) is tracked automatically on
                     the backend and never blocks activating the customer's
                     tracking number. */
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                    <div style={{ fontSize: 13, fontWeight: 900, color: '#15803d',
                      marginBottom: 8 }}>Kifurushi Kimesajiliwa!</div>
                    <div style={{ fontFamily: 'monospace', fontSize: 20, fontWeight: 900,
                      color: '#1d4ed8', marginBottom: 6 }}>
                      {displayParcelNumber(walkResult)}
                    </div>
                    {otherParcelNumbers(walkResult, formatWaybill).length > 0 && (
                      <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#94a3b8', marginBottom: 6 }}>
                        {otherParcelNumbers(walkResult, formatWaybill).join(' · ')}
                      </div>
                    )}
                    {/* The walk-in SMS goes to the SENDER (payment received), never to the recipient. */}
                    {walkResult.senderSmsSent === true && (
                      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
                        SMS ya malipo imetumwa kwa mtumaji
                      </div>
                    )}
                    {walkResult.senderSmsSent === false && (
                      <div style={{ fontSize: 12, color: '#b45309', marginBottom: 8 }}>
                        ⚠️ SMS kwa mtumaji haikutumwa — tuma tena: 🕘 Historia → 🧾 Risiti → 📲 SMS tena
                      </div>
                    )}
                    {walkResult.billing && (
                      <div style={{ fontSize: 12, color: walkResult.billing.isFreeOrder ? '#16a34a' : '#64748b',
                        marginBottom: 20 }}>
                        {walkResult.billing.isFreeOrder
                          ? '🎁 Agizo la bure — halikutozwa ada'
                          : `Ada ya Kentexa TZS ${Number(walkResult.billing.platformFeeCharged).toLocaleString()} imeongezwa kwenye deni lako`}
                      </div>
                    )}
                    {/* Release C, claim code option (a): shown ONCE, only here, only while this
                        screen holds it in memory (never stored), and printed on this first receipt. */}
                    {walkResult.shipmentClaim?.receiptSecret && (
                      <div data-keep-open style={{ border: '2px dashed #7c3aed', borderRadius: 10, padding: '10px 12px', marginBottom: 14, background: '#faf5ff' }}>
                        <div style={{ fontSize: 12, fontWeight: 800, color: '#5b21b6' }}>Namba ya siri ya mteja (kudai mzigo mtandaoni)</div>
                        <div style={{ fontFamily: 'monospace', fontSize: 15, fontWeight: 900, color: '#1e293b', margin: '6px 0', wordSpacing: 4 }}>
                          {formatClaimSecret(walkResult.shipmentClaim.receiptSecret)}
                        </div>
                        <div style={{ fontSize: 11, color: '#6b21a8' }}>
                          Inachapishwa kwenye risiti hii ya kwanza tu. Chapisha risiti na umpe mteja — ukibonyeza "Mzigo mpya" haitaonekana tena.
                        </div>
                      </div>
                    )}
                    {/* Wraps to two rows on a narrow phone instead of squeezing the labels. */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' }}>
                      {walkResult.receipt && (
                        <button onClick={() => handlePrintReceipt(walkResult.receipt, walkResult.shipmentClaim?.receiptSecret || null)}
                          style={{ flex: '1 1 150px', backgroundColor: '#fff', color: '#1d4ed8',
                            border: '2px solid #1d4ed8', padding: '12px 16px', borderRadius: 10,
                            cursor: 'pointer', fontSize: 14, fontWeight: 800, whiteSpace: 'nowrap' }}>
                          🖨️ Chapisha Risiti
                        </button>
                      )}
                      <button onClick={resetWalk}
                        style={{ flex: '1 1 150px', backgroundColor: '#1d4ed8', color: '#fff', border: 'none',
                          padding: '12px 16px', borderRadius: 10, cursor: 'pointer',
                          fontSize: 14, fontWeight: 800, whiteSpace: 'nowrap' }}>
                        + Mzigo mpya
                      </button>
                    </div>
                    {SHOW_CLAIM_CODE_NOTE && !walkResult.shipmentClaim?.receiptSecret && (
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 14 }}>
                        ⚠️ {CLAIM_CODE_NOTE}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 14 }}>
                      Risiti hii inapatikana tena kwenye 🕘 Historia → 🧾 Risiti.
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── B) ONLINE ORDER ──────────────────────────────────────── */}
            {pokeaMode === 'online_order' && (
              <div style={{ backgroundColor: '#fff', borderRadius: 14, padding: 18,
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', marginBottom: 16 }}>
                  <div style={{ fontSize: 15, fontWeight: 900, color: '#1e293b' }}>
                    📱 Pokea Agizo la KenteXa
                  </div>
                  <button onClick={() => setPokeaMode('list')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: 20, color: '#64748b' }}>×</button>
                </div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 14 }}>
                  Muuzaji ameleta parcel. Weka namba ya agizo lake ili kuthibitisha.
                  KenteXa italipa ada ya usafirishaji baada ya uwasilishaji.
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  <input type="text" placeholder="Namba ya Agizo (e.g. 147)"
                    value={onlineOrderId}
                    onChange={e => { setOnlineOrderId(e.target.value); setOrderLookup(null); }}
                    style={{ ...inp, flex: 1 }} />
                  <button onClick={handleLookupOrder} disabled={lookupLoading}
                    style={{ backgroundColor: '#64748b', color: '#fff', border: 'none',
                      padding: '10px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 700 }}>
                    {lookupLoading ? '⏳' : '🔍'}
                  </button>
                </div>

                {orderLookup && (() => {
                  const isCod         = !!orderLookup.isCod && !orderLookup.codBalanceCollected;
                  const isOnlinePaid  = !isCod && orderLookup.source === 'online' && orderLookup.paymentStatus === 'paid';
                  const isOffline     = !isCod && ['offline', 'offline_intercity', 'seller_shipment'].includes(orderLookup.source);
                  const shippingFee   = Number(orderLookup.deliveryFeeAmount || orderLookup.shippingFee || 0);
                  return (
                    <div style={{ borderRadius: 12, padding: 14, marginBottom: 14,
                      border: `2px solid ${isCod ? '#93c5fd' : isOnlinePaid ? '#86efac' : isOffline ? '#fed7aa' : '#e2e8f0'}`,
                      backgroundColor: isCod ? '#eff6ff' : isOnlinePaid ? '#f0fdf4' : isOffline ? '#fff7ed' : '#f8fafc' }}>

                      {/* Payment status header */}
                      {isCod ? (
                        <div style={{ fontSize: 13, fontWeight: 800, color: '#1d4ed8', marginBottom: 10 }}>
                          🚚 Agizo la KenteXa — Malipo Baada ya Kupokea (COD)
                        </div>
                      ) : isOnlinePaid ? (
                        <div style={{ fontSize: 13, fontWeight: 800, color: '#15803d', marginBottom: 10 }}>
                          ✅ Agizo la KenteXa — Imelipiwa
                        </div>
                      ) : isOffline ? (
                        <div style={{ fontSize: 13, fontWeight: 800, color: '#c2410c', marginBottom: 10 }}>
                          💵 Agizo la Nje ya Mtandao — Seller Analipa Cash Sasa
                        </div>
                      ) : (
                        <div style={{ fontSize: 13, fontWeight: 800, color: '#64748b', marginBottom: 10 }}>
                          📋 Agizo Limepatikana
                        </div>
                      )}

                      {/* COD banner — the amount to collect from the BUYER at
                          delivery, distinct from Ada ya Usafirishaji below
                          (which is KenteXa's own shipping fee, unrelated to
                          what the buyer still owes for the goods). */}
                      {isCod && (
                        <div style={{ marginBottom: 10, padding: '10px 12px', borderRadius: 8,
                          backgroundColor: '#dbeafe', border: '1px solid #93c5fd' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#1d4ed8' }}>
                              Kusanya kutoka kwa Mteja
                            </span>
                            <span style={{ fontSize: 18, fontWeight: 900, color: '#1d4ed8' }}>
                              TZS {Number(orderLookup.codRemainingBalance || 0).toLocaleString()}
                            </span>
                          </div>
                          {Number(orderLookup.codUpfrontAmount || 0) > 0 && (
                            <div style={{ fontSize: 11, marginTop: 4, color: '#1e40af' }}>
                              Mteja tayari amelipa TZS {Number(orderLookup.codUpfrontAmount).toLocaleString()} mtandaoni.
                            </div>
                          )}
                        </div>
                      )}

                      {/* Order details */}
                      {[
                        ['Bidhaa',   orderLookup.productName || orderLookup.description || orderLookup.manualProductName || '—'],
                        ['Mteja',    orderLookup.buyerName   || orderLookup.recipientName || '—'],
                        ['Simu',     orderLookup.buyerPhone  || orderLookup.recipientPhone || '—'],
                        ['Kwenda',   orderLookup.destinationCity || orderLookup.deliveryAddress || '—'],
                      ].map(([l, v]) => (
                        <div key={l} style={{ display: 'flex', justifyContent: 'space-between',
                          fontSize: 12, padding: '5px 0',
                          borderBottom: `1px solid ${isOnlinePaid ? '#dcfce7' : isOffline ? '#fed7aa' : '#f1f5f9'}` }}>
                          <span style={{ color: '#64748b' }}>{l}</span>
                          <span style={{ fontWeight: 700, color: '#1e293b' }}>{v}</span>
                        </div>
                      ))}

                      {/* Shipping fee — the most important part */}
                      <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 8,
                        backgroundColor: isOnlinePaid ? '#dcfce7' : isOffline ? '#fed7aa' : '#f1f5f9' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between',
                          alignItems: 'center' }}>
                          <span style={{ fontSize: 12, fontWeight: 700,
                            color: isOnlinePaid ? '#15803d' : isOffline ? '#92400e' : '#475569' }}>
                            Ada ya Usafirishaji
                          </span>
                          <span style={{ fontSize: 18, fontWeight: 900,
                            color: isOnlinePaid ? '#15803d' : isOffline ? '#c2410c' : '#1e293b' }}>
                            {shippingFee > 0 ? `TZS ${shippingFee.toLocaleString()}` : '—'}
                          </span>
                        </div>
                        <div style={{ fontSize: 11, marginTop: 4,
                          color: isOnlinePaid ? '#16a34a' : isOffline ? '#92400e' : '#64748b' }}>
                          {isOnlinePaid
                            ? '💳 KenteXa italipa baada ya uwasilishaji — pokea bila cash'
                            : isOffline
                            ? '💵 Kusanya cash kutoka kwa seller KABLA ya kupokea kifurushi'
                            : ''}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={handleReceiveOnline}
                    disabled={actionLoading || !onlineOrderId.trim()}
                    style={{ flex: 2, backgroundColor: !onlineOrderId.trim() ? '#94a3b8' : '#1d4ed8',
                      color: '#fff', border: 'none', padding: 14, borderRadius: 10,
                      cursor: 'pointer', fontSize: 14, fontWeight: 900 }}>
                    {actionLoading ? '⏳' : '✅ Pokea Hubuni'}
                  </button>
                  <button onClick={() => { setPokeaMode('list'); setOrderLookup(null); }}
                    style={{ flex: 1, background: '#fff', color: '#64748b',
                      border: '2px solid #e2e8f0', padding: 14, borderRadius: 10,
                      cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                    Ghairi
                  </button>
                </div>
              </div>
            )}

            {/* ── C) CONFIRM ARRIVAL ───────────────────────────────────── */}
            {pokeaMode === 'confirm_arrival' && (
              <div style={{ backgroundColor: '#fff', borderRadius: 14, padding: 18,
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', marginBottom: 16 }}>
                  <div style={{ fontSize: 15, fontWeight: 900, color: '#1e293b' }}>
                    🚌 Kifurushi Kimefika Hub
                  </div>
                  <button onClick={() => setPokeaMode('list')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: 20, color: '#64748b' }}>×</button>
                </div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 14 }}>
                  Kifurushi kilichotoka mji mwingine kimefika hub yako.
                  Mpokeaji ataarifiwa — atachagua delivery au self-pickup.
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569',
                    display: 'block', marginBottom: 4 }}>Namba ya Kufuatilia *</label>
                  <input type="text" placeholder="KTX-SHP-42 au KTX-ORD-147"
                    value={arrivalTracking}
                    onChange={e => setArrivalTracking(e.target.value)} style={inp} />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569',
                    display: 'block', marginBottom: 4 }}>Maelezo (hiari)</label>
                  <input type="text" placeholder="e.g. Hali nzuri, imefika salama"
                    value={arrivalNote}
                    onChange={e => setArrivalNote(e.target.value)} style={inp} />
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={handleConfirmArrival}
                    disabled={actionLoading || !arrivalTracking.trim()}
                    style={{ flex: 2, backgroundColor: !arrivalTracking.trim() ? '#94a3b8' : '#7c3aed',
                      color: '#fff', border: 'none', padding: 14, borderRadius: 10,
                      cursor: 'pointer', fontSize: 14, fontWeight: 900 }}>
                    {actionLoading ? '⏳' : '✅ Sajili Kufika — SMS kwa Mpokeaji'}
                  </button>
                  <button onClick={() => setPokeaMode('list')}
                    style={{ flex: 1, background: '#fff', color: '#64748b',
                      border: '2px solid #e2e8f0', padding: 14, borderRadius: 10,
                      cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                    Ghairi
                  </button>
                </div>
              </div>
            )}

            {pokeaMode === 'consolidated' && (
              <div style={{ backgroundColor: '#fff', borderRadius: 14, padding: 18,
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', marginBottom: 16 }}>
                  <div style={{ fontSize: 15, fontWeight: 900, color: '#1e293b' }}>
                    📦 Shehena (Hamisha Vifurushi Vingi)
                  </div>
                  <button onClick={() => { setPokeaMode('list'); resetBulkForm(); setBulkStep('open'); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: 20, color: '#64748b' }}>×</button>
                </div>

                {/* ── Open batches: add to one, or start new ── */}
                {bulkStep === 'open' && (
                  <>
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 14 }}>
                      Kusanya vifurushi siku nzima kwenye shehena moja — mshirika wako atapata
                      ujumbe MMOJA ukiwa na vifurushi vyote, siyo mmoja kwa kila kifurushi.
                    </div>
                    {bulkOpenLoading ? (
                      <div style={{ textAlign: 'center', padding: 20, color: '#94a3b8' }}>⏳</div>
                    ) : bulkOpenShipments.length > 0 && (
                      <>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#d97706', marginBottom: 8 }}>
                          SHEHENA ZILIZO WAZI
                        </div>
                        {bulkOpenShipments.map(s => (
                          <div key={s.id} style={{ backgroundColor: '#fff7ed', borderRadius: 10,
                            padding: '12px 14px', marginBottom: 8, border: '1px solid #fed7aa' }}>
                            <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b' }}>
                              → {s.destinationCity} {s.lastMileSuperAgent?.businessName ? `(${s.lastMileSuperAgent.businessName})` : s.lastMileContactName ? `(${s.lastMileContactName})` : ''}
                            </div>
                            <div style={{ fontSize: 11, color: '#92400e', marginBottom: 8 }}>
                              Vifurushi {s.totalParcels || 0}
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button onClick={() => {
                                setBulkActiveShipment(s); setBulkDestCity(s.destinationCity);
                                setBulkStep('add'); fetchBulkCandidates(s.destinationCity);
                              }}
                                style={{ flex: 1, backgroundColor: '#fff', color: '#d97706', border: '1.5px solid #d97706',
                                  borderRadius: 8, padding: '8px 0', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                                ➕ Ongeza Vifurushi
                              </button>
                              <button onClick={() => { setBulkActiveShipment(s); setBulkStep('dispatch'); }}
                                style={{ flex: 1, backgroundColor: '#16a34a', color: '#fff', border: 'none',
                                  borderRadius: 8, padding: '8px 0', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                                ✅ Maliza na Tuma
                              </button>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                    <button onClick={() => { resetBulkForm(); setBulkStep('new'); }}
                      style={{ width: '100%', marginTop: 8, background: '#fff', color: '#d97706',
                        border: '2px dashed #fdba74', padding: 12, borderRadius: 10,
                        cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                      + Anzisha Shehena Mpya
                    </button>
                  </>
                )}

                {/* ── New batch: destination + last-mile agent + first parcels ── */}
                {bulkStep === 'new' && (
                  <>
                    <div style={{ marginBottom: 10 }}>
                      <label style={{ fontSize: 12, fontWeight: 700, color: '#475569',
                        display: 'block', marginBottom: 4 }}>Mji wa Kuelekea *</label>
                      <IntelligentLocationInput
                        label="" value={bulkDestText} onTextChange={setBulkDestText}
                        onResolved={loc => {
                          if (!loc) return;
                          setBulkLastMileAgentId(null);
                          const cityStr = loc.regionName || loc.districtName || '';
                          setBulkDestCity(cityStr); fetchBulkCandidates(cityStr); fetchBulkHubs(cityStr);
                        }} placeholder="Tafuta eneo la shehena…" required
                      />
                    </div>

                    {bulkHubsLoading ? (
                      <div style={{ fontSize: 12, color: '#94a3b8', padding: '8px 0' }}>⏳ Inatafuta Super Agent...</div>
                    ) : bulkHubs.length > 0 && (
                      <>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#d97706', marginBottom: 8 }}>
                          Super Agent aliyesajiliwa {bulkDestCity}
                        </div>
                        {bulkHubs.map(h => (
                          <label key={h.id} onClick={() => setBulkLastMileAgentId(h.id)}
                            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                              borderRadius: 10, marginBottom: 6, cursor: 'pointer',
                              border: `2px solid ${bulkLastMileAgentId === h.id ? '#d97706' : '#e2e8f0'}`,
                              backgroundColor: bulkLastMileAgentId === h.id ? '#fff7ed' : '#f8fafc' }}>
                            <input type="radio" checked={bulkLastMileAgentId === h.id} readOnly />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b' }}>{h.businessName}</div>
                              <div style={{ fontSize: 11, color: '#64748b' }}>📍 {h.address || h.city}</div>
                            </div>
                          </label>
                        ))}
                      </>
                    )}
                    {!bulkLastMileAgentId && (
                      <>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginTop: 8, marginBottom: 8 }}>
                          AU JAZA MAWASILIANO YA MSHIRIKA (siyo Super Agent aliyesajiliwa)
                        </div>
                        <input type="text" placeholder="Jina la mshirika/biashara"
                          value={bulkManualContact.name}
                          onChange={e => setBulkManualContact(p => ({ ...p, name: e.target.value }))}
                          style={{ ...inp, marginBottom: 8 }} />
                        <input type="text" placeholder="Namba ya simu"
                          value={bulkManualContact.phone}
                          onChange={e => setBulkManualContact(p => ({ ...p, phone: e.target.value }))}
                          style={{ ...inp, marginBottom: 8 }} />
                        <input type="text" placeholder="Mahali/Ofisi (e.g. China Plaza, Ofisi 23, Kariakoo)"
                          value={bulkManualContact.address}
                          onChange={e => setBulkManualContact(p => ({ ...p, address: e.target.value }))}
                          style={{ ...inp, marginBottom: 8 }} />
                      </>
                    )}

                    {bulkLoading ? (
                      <div style={{ textAlign: 'center', padding: 20, color: '#94a3b8' }}>⏳</div>
                    ) : bulkDestCity && bulkCandidates.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: 20, color: '#94a3b8', fontSize: 12 }}>
                        Hakuna vifurushi vilivyopo hubuni kwa mji huu bado.
                      </div>
                    ) : bulkCandidates.length > 0 && (
                      <>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', marginTop: 10, marginBottom: 8 }}>
                          Chagua vifurushi ({bulkSelected.size}/{bulkCandidates.length})
                        </div>
                        {bulkCandidates.map(p => (
                          <label key={p.trackingNumber} style={{ display: 'flex', alignItems: 'center',
                            gap: 10, padding: '10px 12px', borderRadius: 10, marginBottom: 6,
                            backgroundColor: bulkSelected.has(p.trackingNumber) ? '#fef3c7' : '#f8fafc',
                            cursor: 'pointer' }}>
                            <input type="checkbox" checked={bulkSelected.has(p.trackingNumber)}
                              onChange={e => setBulkSelected(prev => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(p.trackingNumber); else next.delete(p.trackingNumber);
                                return next;
                              })} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 12, fontWeight: 800, fontFamily: 'monospace', color: '#1d4ed8' }}>
                                {displayParcelNumber(p)}
                              </div>
                              <div style={{ fontSize: 11, color: '#64748b' }}>
                                {p.recipientName} — {p.description || 'Bidhaa'}
                              </div>
                            </div>
                          </label>
                        ))}
                      </>
                    )}
                    <button onClick={handleCreateBulkShipment}
                      disabled={actionLoading || !bulkSelected.size || (!bulkLastMileAgentId && !bulkManualContact.name)}
                      style={{ width: '100%', marginTop: 10,
                        backgroundColor: (!bulkSelected.size || (!bulkLastMileAgentId && !bulkManualContact.name)) ? '#94a3b8' : '#d97706',
                        color: '#fff', border: 'none', padding: 14, borderRadius: 10,
                        cursor: 'pointer', fontSize: 14, fontWeight: 900 }}>
                      {actionLoading ? '⏳' : `📦 Unda Shehena (${bulkSelected.size}) — SMS kwa Wanunuzi`}
                    </button>
                    <button onClick={() => { resetBulkForm(); setBulkStep('open'); }}
                      style={{ width: '100%', marginTop: 8, background: '#fff', color: '#64748b',
                        border: '2px solid #e2e8f0', padding: 10, borderRadius: 10,
                        cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                      Rudi
                    </button>
                  </>
                )}

                {/* ── Add more parcels to an already-open batch ── */}
                {bulkStep === 'add' && bulkActiveShipment && (
                  <>
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 14 }}>
                      Kuongeza kwenye shehena → {bulkActiveShipment.destinationCity}
                      {bulkActiveShipment.lastMileSuperAgent?.businessName ? ` (${bulkActiveShipment.lastMileSuperAgent.businessName})` : ''}
                    </div>
                    {bulkLoading ? (
                      <div style={{ textAlign: 'center', padding: 20, color: '#94a3b8' }}>⏳</div>
                    ) : bulkCandidates.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: 20, color: '#94a3b8', fontSize: 12 }}>
                        Hakuna vifurushi vingine vilivyopo hubuni kwa mji huu bado.
                      </div>
                    ) : (
                      <>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>
                          Chagua vifurushi ({bulkSelected.size}/{bulkCandidates.length})
                        </div>
                        {bulkCandidates.map(p => (
                          <label key={p.trackingNumber} style={{ display: 'flex', alignItems: 'center',
                            gap: 10, padding: '10px 12px', borderRadius: 10, marginBottom: 6,
                            backgroundColor: bulkSelected.has(p.trackingNumber) ? '#fef3c7' : '#f8fafc',
                            cursor: 'pointer' }}>
                            <input type="checkbox" checked={bulkSelected.has(p.trackingNumber)}
                              onChange={e => setBulkSelected(prev => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(p.trackingNumber); else next.delete(p.trackingNumber);
                                return next;
                              })} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 12, fontWeight: 800, fontFamily: 'monospace', color: '#1d4ed8' }}>
                                {displayParcelNumber(p)}
                              </div>
                              <div style={{ fontSize: 11, color: '#64748b' }}>
                                {p.recipientName} — {p.description || 'Bidhaa'}
                              </div>
                            </div>
                          </label>
                        ))}
                      </>
                    )}
                    <button onClick={handleAddToShipment}
                      disabled={actionLoading || !bulkSelected.size}
                      style={{ width: '100%', marginTop: 10, backgroundColor: !bulkSelected.size ? '#94a3b8' : '#d97706',
                        color: '#fff', border: 'none', padding: 14, borderRadius: 10,
                        cursor: 'pointer', fontSize: 14, fontWeight: 900 }}>
                      {actionLoading ? '⏳' : `➕ Ongeza (${bulkSelected.size}) — SMS kwa Wanunuzi`}
                    </button>
                    <button onClick={() => { resetBulkForm(); setBulkStep('open'); fetchOpenShipments(); }}
                      style={{ width: '100%', marginTop: 8, background: '#fff', color: '#64748b',
                        border: '2px solid #e2e8f0', padding: 10, borderRadius: 10,
                        cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                      Rudi
                    </button>
                  </>
                )}

                {/* ── Finalize: transport info only, one consolidated agent SMS ── */}
                {bulkStep === 'dispatch' && bulkActiveShipment && (
                  <>
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 14 }}>
                      Shehena → {bulkActiveShipment.destinationCity}
                      {bulkActiveShipment.lastMileSuperAgent?.businessName ? ` (${bulkActiveShipment.lastMileSuperAgent.businessName})` : ''}.
                      Taarifa za usafiri ni hiari — ujumbe MMOJA utatumwa kwa mshirika ukiwa na
                      vifurushi vyote vya shehena hii.
                    </div>
                    <input type="text" placeholder="Kampuni ya usafiri (hiari — e.g. Shabiby Bus)"
                      value={bulkTransport.transportCompany}
                      onChange={e => setBulkTransport(p => ({ ...p, transportCompany: e.target.value }))}
                      style={{ ...inp, marginBottom: 8 }} />
                    <input type="text" placeholder="Namba ya Tiketi/Rejea (hiari)"
                      value={bulkTransport.transportRef}
                      onChange={e => setBulkTransport(p => ({ ...p, transportRef: e.target.value }))}
                      style={{ ...inp, marginBottom: 8 }} />
                    <input type="number" placeholder="Gharama ya Usafiri (TZS, hiari)"
                      value={bulkTransport.totalShippingCost}
                      onChange={e => setBulkTransport(p => ({ ...p, totalShippingCost: e.target.value }))}
                      style={{ ...inp, marginBottom: 8 }} />

                    <button onClick={handleDispatchBulkShipment} disabled={actionLoading}
                      style={{ width: '100%', marginTop: 10, backgroundColor: '#16a34a',
                        color: '#fff', border: 'none', padding: 14, borderRadius: 10,
                        cursor: 'pointer', fontSize: 14, fontWeight: 900 }}>
                      {actionLoading ? '⏳' : '✅ Tuma Shehena — Ujumbe kwa Mshirika'}
                    </button>
                    <button onClick={() => { resetBulkForm(); setBulkStep('open'); fetchOpenShipments(); }}
                      style={{ width: '100%', marginTop: 8, background: '#fff', color: '#64748b',
                        border: '2px solid #e2e8f0', padding: 10, borderRadius: 10,
                        cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                      Rudi
                    </button>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {/* ════════════════════════════════════════════════════════════════
            📤 TUMA TAB
            ════════════════════════════════════════════════════════════════ */}
        {activeTab === 'tuma' && (
          <>
            {/* Dispatch form — shown when parcel selected */}
            {dispatchParcel ? (
              <div style={{ backgroundColor: '#fff', borderRadius: 14, padding: 18,
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontSize: 15, fontWeight: 900, color: '#1e293b' }}>📤 Tuma</div>
                  <button onClick={() => setDispatchParcel(null)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: 20, color: '#64748b' }}>×</button>
                </div>
                <div style={{ fontSize: 12, fontFamily: 'monospace', color: '#1d4ed8',
                  marginBottom: 14 }}>
                  {displayParcelNumber(dispatchParcel)} → {dispatchParcel.destinationCity}
                  {dispatchParcel.transitCity && <span style={{ color: '#f59e0b' }}>
                    {' '}via {dispatchParcel.transitCity}
                  </span>}
                </div>

                {/* Mode toggle — C2: an intercity parcel goes by bus/courier only; the
                    local agent choice is for same-city parcels (and the backend refuses it too). */}
                {!isIntercityPlace(dispatchParcel.originCity || profile?.city, dispatchParcel.destinationCity) && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                  {[
                    { k: 'transport', l: '🚌 Basi / Courier' },
                    { k: 'agent',     l: '🏍️ Wakala wa Mtaa' },
                  ].map(m => (
                    <button key={m.k} onClick={() => setDispatchMode(m.k)}
                      style={{ flex: 1, padding: '9px 8px', borderRadius: 8,
                        cursor: 'pointer', fontSize: 12, fontWeight: 700, border: 'none',
                        backgroundColor: dispatchMode === m.k ? '#1d4ed8' : '#f1f5f9',
                        color: dispatchMode === m.k ? '#fff' : '#475569' }}>
                      {m.l}
                    </button>
                  ))}
                </div>
                )}

                {dispatchMode === 'transport' && (
                  <>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                      {[{ k: 'bus', l: '🚌 Basi' }, { k: 'courier', l: '📦 Courier' }].map(t => (
                        <button key={t.k}
                          onClick={() => setDispatchForm(f => ({ ...f, transportType: t.k }))}
                          style={{ flex: 1, padding: 8, borderRadius: 8, cursor: 'pointer',
                            fontSize: 12, fontWeight: 700,
                            border: dispatchForm.transportType === t.k
                              ? '2px solid #1d4ed8' : '2px solid #e2e8f0',
                            backgroundColor: dispatchForm.transportType === t.k ? '#eff6ff' : '#fff',
                            color: dispatchForm.transportType === t.k ? '#1d4ed8' : '#64748b' }}>
                          {t.l}
                        </button>
                      ))}
                    </div>

                    {dispatchForm.transportType === 'bus' && (
                      <>
                        <input type="text" placeholder="Jina la Basi (e.g. Abood, Tahmeed, Scandinavian...)"
                          value={dispatchForm.busCompany}
                          onChange={e => setDispatchForm(f => ({ ...f, busCompany: e.target.value }))}
                          style={{ ...inp, marginBottom: 8 }} />
                        <input type="text" placeholder="Namba ya Tiketi *"
                          value={dispatchForm.busTicketNumber}
                          onChange={e => setDispatchForm(f => ({ ...f, busTicketNumber: e.target.value }))}
                          style={{ ...inp, marginBottom: 8 }} />
                        <input type="text" placeholder="Saa ya Kuondoka (e.g. 6am)"
                          value={dispatchForm.busDeparture}
                          onChange={e => setDispatchForm(f => ({ ...f, busDeparture: e.target.value }))}
                          style={{ ...inp, marginBottom: 8 }} />
                      </>
                    )}
                    {dispatchForm.transportType === 'courier' && (
                      <>
                        <input type="text" placeholder="Jina la Courier"
                          value={dispatchForm.courierName}
                          onChange={e => setDispatchForm(f => ({ ...f, courierName: e.target.value }))}
                          style={{ ...inp, marginBottom: 8 }} />
                        <input type="text" placeholder="Namba ya Kufuatilia"
                          value={dispatchForm.courierTrackingRef}
                          onChange={e => setDispatchForm(f => ({ ...f, courierTrackingRef: e.target.value }))}
                          style={{ ...inp, marginBottom: 8 }} />
                      </>
                    )}

                    {/* Hub-to-hub hand-off — send to another Super Agent's hub
                        in the destination city, who then arranges last-mile
                        delivery to the customer themselves. Optional: skip
                        this and the parcel just travels to the buyer directly. */}
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8',
                      marginTop: 10, marginBottom: 6, textTransform: 'uppercase' }}>
                      Chagua Super Agent Atakayepokea (hiari)
                    </div>
                    {loadingHubs ? (
                      <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>⏳ Inatafuta...</div>
                    ) : destinationHubs.length === 0 ? (
                      <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>
                        Hakuna Super Agent mwingine katika {dispatchParcel.destinationCity}
                      </div>
                    ) : (
                      <div style={{ marginBottom: 8 }}>
                        {destinationHubs.map(h => (
                          <div key={h.id}
                            onClick={() => setDispatchForm(f => ({ ...f,
                              destinationSuperAgentId: f.destinationSuperAgentId === h.id ? null : h.id }))}
                            style={{ padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                              marginBottom: 6,
                              border: dispatchForm.destinationSuperAgentId === h.id
                                ? '2px solid #7c3aed' : '1px solid #e2e8f0',
                              backgroundColor: dispatchForm.destinationSuperAgentId === h.id ? '#f5f3ff' : '#fff' }}>
                            <div style={{ fontSize: 13, fontWeight: 700 }}>
                              {h.businessName} {dispatchForm.destinationSuperAgentId === h.id && '✅'}
                            </div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>
                              ⭐ {Number(h.rating || 5).toFixed(1)} · {h.address || h.city}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Optional — only fields actually filled in get included in the receiver SMS */}
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8',
                      marginTop: 6, marginBottom: 6, textTransform: 'uppercase' }}>
                      Maelezo ya ziada (hiari)
                    </div>
                    <input type="text" placeholder="Jina la Dereva (hiari)"
                      value={dispatchForm.driverName}
                      onChange={e => setDispatchForm(f => ({ ...f, driverName: e.target.value }))}
                      style={{ ...inp, marginBottom: 8 }} />
                    <input type="text" placeholder="Namba ya Simu ya Dereva (hiari)"
                      value={dispatchForm.driverPhone}
                      onChange={e => setDispatchForm(f => ({ ...f, driverPhone: e.target.value }))}
                      style={{ ...inp, marginBottom: 8 }} />
                    <input type="text" placeholder="Namba ya Gari (hiari)"
                      value={dispatchForm.vehicleNumber}
                      onChange={e => setDispatchForm(f => ({ ...f, vehicleNumber: e.target.value }))}
                      style={{ ...inp, marginBottom: 8 }} />
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <input type="date" placeholder="Tarehe ya Kuondoka"
                        value={dispatchForm.departureDate}
                        onChange={e => setDispatchForm(f => ({ ...f, departureDate: e.target.value }))}
                        style={{ ...inp, flex: 1 }} />
                      <input type="time" placeholder="Saa ya Kuondoka"
                        value={dispatchForm.departureTime}
                        onChange={e => setDispatchForm(f => ({ ...f, departureTime: e.target.value }))}
                        style={{ ...inp, flex: 1 }} />
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <input type="date" placeholder="Tarehe ya Kufika"
                        value={dispatchForm.expectedArrivalDate}
                        onChange={e => setDispatchForm(f => ({ ...f, expectedArrivalDate: e.target.value }))}
                        style={{ ...inp, flex: 1 }} />
                      <input type="time" placeholder="Saa ya Kufika"
                        value={dispatchForm.expectedArrivalTime}
                        onChange={e => setDispatchForm(f => ({ ...f, expectedArrivalTime: e.target.value }))}
                        style={{ ...inp, flex: 1 }} />
                    </div>
                  </>
                )}

                {dispatchMode === 'agent' && (
                  <>
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
                      Chagua wakala wa mtaa kwa uwasilishaji wa mwisho
                      katika {dispatchParcel.destinationCity}
                    </div>
                    {localAgents.length === 0 ? (
                      <div style={{ backgroundColor: '#fff7ed', borderRadius: 8,
                        padding: 12, fontSize: 12, color: '#c2410c' }}>
                        Hakuna mawakala walioidhinishwa katika {dispatchParcel.destinationCity}
                      </div>
                    ) : (
                      localAgents.map(a => (
                        <div key={a.id}
                          onClick={() => setSelectedAgent(selectedAgent?.id === a.id ? null : a)}
                          style={{ padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                            marginBottom: 8,
                            border: selectedAgent?.id === a.id
                              ? '2px solid #16a34a' : '1px solid #e2e8f0',
                            backgroundColor: selectedAgent?.id === a.id ? '#f0fdf4' : '#fff' }}>
                          <div style={{ fontSize: 13, fontWeight: 700 }}>
                            {a.fullName} {selectedAgent?.id === a.id && '✅'}
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>
                            ⭐ {Number(a.rating || 5).toFixed(1)} ·
                            TZS {Number(a.deliveryFee || 1000).toLocaleString()}
                          </div>
                        </div>
                      ))
                    )}
                  </>
                )}

                <input type="text" placeholder="Maelezo ya ziada (hiari)"
                  value={dispatchForm.notes}
                  onChange={e => setDispatchForm(f => ({ ...f, notes: e.target.value }))}
                  style={{ ...inp, marginBottom: 14, marginTop: 8 }} />

                <button onClick={handleDispatch} disabled={actionLoading}
                  style={{ width: '100%', backgroundColor: '#16a34a', color: '#fff',
                    border: 'none', padding: 14, borderRadius: 10, cursor: 'pointer',
                    fontSize: 14, fontWeight: 900 }}>
                  {actionLoading ? '⏳' : '📤 Tuma Sasa'}
                </button>
              </div>
            ) : (
              <>
                {/* Ready to dispatch */}
                {toDispatch.length > 0 && (
                  <>
                    <div style={{ fontSize: 12, fontWeight: 800, color: '#16a34a',
                      marginBottom: 8 }}>
                      📤 TAYARI KUTUMA ({toDispatch.length})
                    </div>
                    {toDispatch.map(p => (
                      <PCard key={p.trackingNumber} p={p} actions={p.transportMethod === 'van' ? [
                        { label: '🚐 Fungua Van Desk', color: '#1d4ed8', fn: () => { setActiveTab('van'); fetchVanDesk(); } },
                      ] : isDirectLocalParcel(p) ? [
                        { label: '🏍️ Chagua Agent', color: '#16a34a', fn: (parcel) => {
                          setDispatchParcel(parcel); setSelectedAgent(null); setDispatchMode('agent');
                        }},
                      ] : [
                        { label: '🏢 Hamisha Hub', color: '#7c3aed', fn: (parcel) => setTransferModal(parcel) },
                        { label: '🚌 Panga Usafiri', color: '#1d4ed8', fn: (parcel) => openAssignTransport(
                          parcel.trackingNumber, parcel.originCity || profile?.city || 'Dar es Salaam', parcel.destinationCity) },
                        { label: '📤 Tuma', color: '#16a34a', fn: (parcel) => {
                          setDispatchParcel(parcel);
                          setDispatchForm({ transportType: 'bus', busCompany: '', busTicketNumber: '', busDeparture: '', courierName: '', courierTrackingRef: '', notes: '', driverName: '', driverPhone: '', vehicleNumber: '', departureDate: '', departureTime: '', expectedArrivalDate: '', expectedArrivalTime: '', destinationSuperAgentId: null });
                          setSelectedAgent(null); setDispatchMode('transport'); fetchDestinationHubs(parcel.destinationCity);
                        }},
                      ]} />
                    ))}
                  </>
                )}

                {/* In transit */}
                {inTransit.length > 0 && (
                  <>
                    <div style={{ fontSize: 12, fontWeight: 800, color: '#ca8a04',
                      marginBottom: 8, marginTop: 16 }}>
                      🚌 NJIANI ({inTransit.length})
                    </div>
                    {inTransit.map(p => (
                      <PCard key={p.trackingNumber} p={p} actions={[
                        { label: 'Sasisha', color: '#64748b',
                          fn: (parcel) => { setStatusParcel(parcel); setNewStatus(''); setPickupCode(''); setCodBalanceAmount(''); } },
                         { label: confirmSending[p.orderId] ? '⏳...' : '📲 Thibitisha', // eslint-disable-line no-undef
                          color: '#16a34a',
                           fn: (parcel) => handleSendConfirmLink(parcel.orderId) }, // eslint-disable-line no-undef
                      ]} />
                    ))}
                  </>
                )}

                {toDispatch.length === 0 && inTransit.length === 0 && (
                  <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
                    <div style={{ fontSize: 36, marginBottom: 10 }}>✅</div>
                    Vifurushi vyote vimetumwa
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* ════════════════════════════════════════════════════════════════
            🕘 HISTORIA TAB — every parcel this hub has ever touched, any
            status, either role. Previously a delivered/closed-out parcel
            had no browsable list at all, only a bare count on Mapato.
            ════════════════════════════════════════════════════════════════ */}
        {activeTab === 'historia' && (
          <>
            <input type="text" placeholder="🔍 Tafuta kwa namba, jina au simu..."
              value={historiaSearch}
              onChange={e => setHistoriaSearch(e.target.value)}
              style={{ ...inp, marginBottom: 14 }} />
            {historiaList.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
                <div style={{ fontSize: 36, marginBottom: 10 }}>📭</div>
                {historiaSearch ? 'Hakuna kilicholingana' : 'Hakuna vifurushi bado'}
              </div>
            ) : (
              historiaList.map(p => (
                <PCard key={p.trackingNumber} p={p} actions={[
                  ...(canReopenReceipt(p) ? [{ label: '🧾 Risiti', color: '#1d4ed8',
                    fn: (parcel) => openReceipt(parcel.trackingNumber) }] : []),
                  { label: '🕘 Historia Kamili', color: '#7c3aed',
                    fn: (parcel) => openParcelHistory(parcel.trackingNumber) },
                ]} />
              ))
            )}
          </>
        )}

        {/* ════════════════════════════════════════════════════════════════
            💰 MAPATO TAB
            ════════════════════════════════════════════════════════════════ */}
        {activeTab === 'mapato' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
              <div style={{ backgroundColor: '#fff', borderRadius: 14, padding: 18,
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, marginBottom: 6 }}>
                  💵 MAPATO YA LEO
                </div>
                <div style={{ fontSize: 22, fontWeight: 900, color: '#16a34a' }}>
                  TZS {Number(revenue?.today ?? cashToday).toLocaleString()}
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                  Umekusanya moja kwa moja
                </div>
              </div>
              <div style={{ backgroundColor: '#fff', borderRadius: 14, padding: 18,
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, marginBottom: 6 }}>
                  💳 KENTEXA INAKUDAIWA
                </div>
                <div style={{ fontSize: 22, fontWeight: 900, color: '#1d4ed8' }}>
                  TZS {kenetxaOwes.toLocaleString()}
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                  Ada ya usafirishaji — online orders
                </div>
              </div>
            </div>

            <div style={{ backgroundColor: '#fff', borderRadius: 14, padding: 16,
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 12 }}>
                📊 Muhtasari — Data Halisi
              </div>
              {[
                ['Wiki hii', `TZS ${Number(revenue?.thisWeek || 0).toLocaleString()}`, '📅'],
                ['Mwezi huu', `TZS ${Number(revenue?.thisMonth || 0).toLocaleString()}`, '🗓️'],
                ['Mapato yote (jumla)', `TZS ${Number(revenue?.total ?? dashData?.stats?.totalEarnings ?? 0).toLocaleString()}`, '💰'],
                ['Vifurushi vyote', dashData?.stats?.totalParcels || 0, '📦'],
                ['Vimetumwa', revenue?.shippedCount ?? 0, '🚚'],
                ['Vimefikishwa', revenue?.completedCount ?? dashData?.stats?.delivered ?? 0, '✅'],
              ].map(([l, v, icon]) => (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between',
                  padding: '8px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}>
                  <span style={{ color: '#64748b' }}>{icon} {l}</span>
                  <span style={{ fontWeight: 800, color: '#1e293b' }}>{v}</span>
                </div>
              ))}
            </div>

            {/* Referral program (Phase 4) */}
            {referralData && (
              <div style={{ backgroundColor: '#fff', borderRadius: 14, padding: 16,
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 12 }}>
                  🎁 Referral — Wape Wenzako Waje
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ flex: 1, backgroundColor: '#f8fafc', borderRadius: 8, padding: '10px 12px',
                    fontSize: 13, fontWeight: 800, color: '#1d4ed8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {referralData.referralCode || '—'}
                  </div>
                  <button
                    onClick={() => {
                      const link = `https://kentexa.com/?ref=${referralData.referralCode}`;
                      navigator.clipboard?.writeText(link).then(() => {
                        setRefLinkCopied(true);
                        setTimeout(() => setRefLinkCopied(false), 2000);
                      });
                    }}
                    disabled={!referralData.referralCode}
                    style={{ backgroundColor: refLinkCopied ? '#16a34a' : '#1d4ed8', color: '#fff', border: 'none',
                      padding: '10px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {refLinkCopied ? '✅ Copied' : '🔗 Copy Link'}
                  </button>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0',
                  borderTop: '1px solid #f1f5f9', fontSize: 13 }}>
                  <span style={{ color: '#64748b' }}>📦 Free Orders Available</span>
                  <span style={{ fontWeight: 900, color: '#16a34a' }}>
                    {Math.max(0, Number(referralData.freeOrdersGranted || 0) - Number(referralData.freeOrdersUsed || 0))}
                  </span>
                </div>
                {referralData.referrals?.length > 0 && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f1f5f9' }}>
                    {referralData.referrals.map(r => {
                      const badge = { registered: ['⏳', '#ca8a04'], qualified: ['✅', '#16a34a'], rejected_fraud: ['❌', '#dc2626'] }[r.status] || ['?', '#64748b'];
                      return (
                        <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between',
                          padding: '5px 0', fontSize: 12 }}>
                          <span style={{ color: '#475569' }}>{r.referredUser?.name || '—'}</span>
                          <span style={{ color: badge[1], fontWeight: 700 }}>{badge[0]} {r.status}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {revenue?.monthly?.length > 0 && (
              <div style={{ backgroundColor: '#fff', borderRadius: 14, padding: 16,
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 12 }}>
                  🗓️ Mapato kwa Mwezi
                </div>
                {revenue.monthly.map(m => (
                  <div key={m.month} style={{ display: 'flex', justifyContent: 'space-between',
                    padding: '8px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}>
                    <span style={{ color: '#64748b' }}>
                      {new Date(`${m.month}-01T00:00:00`).toLocaleDateString('sw-TZ', { year: 'numeric', month: 'long' })}
                      {' '}({m.orders} {m.orders === 1 ? 'agizo' : 'maagizo'})
                    </span>
                    <span style={{ fontWeight: 800, color: '#16a34a' }}>
                      TZS {Number(m.revenue).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Founding-pilot free orders + real billing balance */}
            {dashData?.agent?.billing && (
              <div style={{ backgroundColor: '#fff', borderRadius: 14, padding: 16,
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: 14,
                border: dashData.agent.billing.billingBlocked ? '2px solid #dc2626' : 'none' }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 12 }}>
                  🎁 Faida ya Super Agent Mwanzilishi
                </div>
                {[
                  ['Agizo la bure lililotolewa', dashData.agent.billing.freeOrdersGranted],
                  ['Agizo la bure lililotumika', dashData.agent.billing.freeOrdersUsed],
                  ['Yamebaki bure', dashData.agent.billing.freeOrdersRemaining],
                  ['Maagizo yaliyolipiwa', dashData.agent.billing.paidOrders],
                  ['Ada kwa kila agizo', `TZS ${Number(dashData.agent.billing.platformFeePerOrder).toLocaleString()}`],
                ].map(([l, v]) => (
                  <div key={l} style={{ display: 'flex', justifyContent: 'space-between',
                    padding: '8px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}>
                    <span style={{ color: '#64748b' }}>{l}</span>
                    <span style={{ fontWeight: 800, color: '#1e293b' }}>{v}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between',
                  padding: '10px 0 0', fontSize: 14 }}>
                  <span style={{ color: '#64748b', fontWeight: 700 }}>Deni lililobaki</span>
                  <span style={{ fontWeight: 900,
                    color: dashData.agent.billing.billingBlocked ? '#dc2626' : '#1e293b' }}>
                    TZS {Number(dashData.agent.billing.outstandingBalance).toLocaleString()}
                    {' '}/ {Number(dashData.agent.billing.billingThreshold).toLocaleString()}
                  </span>
                </div>
                {dashData.agent.billing.billingBlocked && (
                  <div style={{ marginTop: 10, backgroundColor: '#fef2f2', borderRadius: 8,
                    padding: '10px 12px', fontSize: 12, color: '#b91c1c', fontWeight: 700 }}>
                    ⚠️ Huduma imesimamishwa hadi deni lilipwe. Wasiliana na Kentexa kulipa deni lako.
                  </div>
                )}
                {!dashData.agent.billing.billingBlocked && (
                  <div style={{ marginTop: 10, backgroundColor: '#f0fdf4', borderRadius: 8,
                    padding: '10px 12px', fontSize: 12, color: '#166534' }}>
                    {dashData.agent.billing.freeOrdersRemaining > 0
                      ? `ℹ️ Umebaki na maagizo ${dashData.agent.billing.freeOrdersRemaining} ya bure. Baada ya kumaliza, kila agizo jipya litatozwa TZS ${Number(dashData.agent.billing.platformFeePerOrder).toLocaleString()}.`
                      : `ℹ️ Umeshamaliza maagizo yako 50 ya bure. Sasa kila agizo jipya litatozwa TZS ${Number(dashData.agent.billing.platformFeePerOrder).toLocaleString()}.`}
                  </div>
                )}
              </div>
            )}

            {/* Online orders pending payout */}
            {parcels.filter(p => p.source === 'online' && !p.agentPaidOut && p.status === 'delivered').length > 0 && (
              <>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#1d4ed8', marginBottom: 8 }}>
                  💳 ONLINE ORDERS — KenteXa Itakulipa
                </div>
                {parcels.filter(p => p.source === 'online' && !p.agentPaidOut).map(p => (
                  <div key={p.trackingNumber} style={{ backgroundColor: '#eff6ff',
                    borderRadius: 10, padding: '10px 14px', marginBottom: 8,
                    display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontSize: 12, fontFamily: 'monospace', color: '#1d4ed8',
                        fontWeight: 700 }}>{displayParcelNumber(p)}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>
                        {p.originCity} → {p.destinationCity} · <SBadge status={p.status} />
                      </div>
                    </div>
                    <div style={{ fontWeight: 900, color: '#1d4ed8', fontSize: 14 }}>
                      TZS {Number(p.actualShippingFee || p.superAgentEarnings || 0).toLocaleString()}
                    </div>
                  </div>
                ))}
              </>
            )}
          </>
        )}

        {/* ════════════════════════════════════════════════════════════════
            📋 BEI TAB — Rate Card
            ════════════════════════════════════════════════════════════════ */}
        {activeTab === 'bei' && (
          <>
            <div style={{ backgroundColor: '#fff7ed', borderRadius: 10,
              padding: '10px 14px', marginBottom: 14, fontSize: 12, color: '#92400e' }}>
              💡 Orodha hii inakusaidia kujibu haraka mtu anayeuliza bei ya kutuma mzigo
            </div>
            {rates.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>📋</div>
                Bado haujaweka bei zako.
                <br />
                <button onClick={() => onNavigate('SuperAgentSettings')}
                  style={{ marginTop: 12, backgroundColor: '#1d4ed8', color: '#fff',
                    border: 'none', padding: '10px 20px', borderRadius: 8,
                    cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                  + Weka Bei Zangu
                </button>
              </div>
            ) : (
              <>
                <div style={{ backgroundColor: '#fff', borderRadius: 14, overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                    backgroundColor: '#f8fafc', padding: '10px 14px',
                    fontSize: 11, fontWeight: 800, color: '#64748b' }}>
                    <span>MJI</span><span>KWA KG</span><span>BEI YA CHINI</span>
                  </div>
                  {rates.map((r, i) => (
                    <div key={r.id} style={{ display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      padding: '10px 14px', fontSize: 13,
                      backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa',
                      borderTop: '1px solid #f1f5f9' }}>
                      <span style={{ fontWeight: 700, color: '#1e293b' }}>
                        {r.destinationCity}
                      </span>
                      <span style={{ color: '#64748b' }}>
                        TZS {Number(r.perKgFee || 0).toLocaleString()}
                      </span>
                      <span style={{ color: '#16a34a', fontWeight: 700 }}>
                        TZS {Number(r.baseShippingFee || 0).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
                <button onClick={() => onNavigate('SuperAgentSettings')}
                  style={{ width: '100%', marginTop: 12, background: '#fff',
                    color: '#1d4ed8', border: '2px solid #1d4ed8', padding: 12,
                    borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                  ✏️ Hariri Bei
                </button>
              </>
            )}
          </>
        )}

        {/* ════════════════════════════════════════════════════════════════
            🚐 VAN YA LEO TAB (Dar only)
            ════════════════════════════════════════════════════════════════ */}
        {activeTab === 'van' && isDar && (
          <div>
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:18, fontWeight:900, color:'#1e293b' }}>🚐 Van Desk</div>
              <div style={{ fontSize:12, color:'#64748b', marginTop:4 }}>Pokea mizigo iliyoshushwa na Van, kisha endelea na pickup au delivery.</div>
            </div>
            {vanDeskLoading && <div style={{ padding:20, textAlign:'center', color:'#64748b' }}>Inapakia...</div>}
            {!vanDeskLoading && (
              <>
                <div style={{ fontSize:12, fontWeight:900, color:'#1d4ed8', marginBottom:8 }}>READY TO SEND · {vanToSend.length}</div>
                {vanToSend.length === 0 ? <div style={{ background:'#fff', padding:14, borderRadius:12, color:'#94a3b8', fontSize:12, marginBottom:18 }}>Hakuna mzigo uliopo custody yako unaosubiri Van.</div> :
                  vanToSend.map(p => {
                    const candidates=eligibleRuns[p.id] || [];
                    return <div key={p.id} style={{ background:'#fff', padding:14, borderRadius:12, marginBottom:8 }}>
                      <div style={{fontWeight:900,fontSize:13}}>{p.trackingNumber || `Parcel #${p.id}`}</div>
                      <div style={{fontSize:11,color:'#64748b',margin:'3px 0 8px'}}>{p.originCity} → {p.destinationCity}{p.weightKg ? ` · ${p.weightKg}kg` : ''}</div>
                      {candidates.length===0 ? <button onClick={()=>loadEligibleRuns(p.id)} style={{width:'100%',border:'none',borderRadius:8,padding:9,fontWeight:800,cursor:'pointer'}}>Tafuta Van</button> :
                        <><select value={selectedRun[p.id] || ''} onChange={e=>setSelectedRun(x=>({...x,[p.id]:e.target.value}))} style={{width:'100%',padding:9,borderRadius:8,border:'1px solid #cbd5e1',marginBottom:7}}>
                          <option value="">Chagua Van</option>{candidates.map(x=><option key={x.runId} value={x.runId}>{x.loadLabel} → {x.unloadLabel} · {new Date(x.scheduledDeparture).toLocaleString()}</option>)}
                        </select>
                        <button disabled={!selectedRun[p.id]||actionLoading} onClick={()=>releaseParcelToVan(p)} style={{width:'100%',border:'none',borderRadius:8,padding:9,background:'#1d4ed8',color:'#fff',fontWeight:900}}>Achia kwa Van</button></>}
                    </div>;
                  })}
                <div style={{ fontSize:12, fontWeight:900, color:'#7c3aed', margin:'18px 0 8px' }}>INCOMING VAN · {vanIncoming.length}</div>
                {vanIncoming.length === 0 ? <div style={{ background:'#fff', padding:14, borderRadius:12, color:'#94a3b8', fontSize:12, marginBottom:18 }}>Hakuna mzigo wa Van unaosubiri kupokelewa.</div> :
                  vanIncoming.map(p => <div key={p.assignmentId} style={{ background:'#fff', padding:14, borderRadius:12, marginBottom:8 }}>
                    <div style={{ fontWeight:900, fontSize:13, color:'#1e293b' }}>{p.trackingNumber || `Parcel #${p.parcelId}`}</div>
                    <div style={{ fontSize:11, color:'#64748b', margin:'4px 0 10px' }}>Run #{p.runId} · Imeshushwa {Math.max(0, Math.round(Number(p.waitingMinutes || 0)))} dk zilizopita</div>
                    <button disabled={actionLoading} onClick={() => confirmVanReceipt(p.assignmentId)}
                      style={{ width:'100%', border:'none', borderRadius:10, padding:11, background:'#16a34a', color:'#fff', fontWeight:900, cursor:'pointer' }}>
                      ✓ Nimepokea Mzigo
                    </button>
                  </div>)}
                <div style={{ fontSize:12, fontWeight:900, color:'#1d4ed8', margin:'18px 0 8px' }}>IKO HUBUNI · {vanReady.length}</div>
                {vanReady.length === 0 ? <div style={{ background:'#fff', padding:14, borderRadius:12, color:'#94a3b8', fontSize:12 }}>Hakuna mzigo wa Van unaosubiri pickup/delivery.</div> :
                  vanReady.map(p => <div key={p.id} style={{ background:'#fff', padding:14, borderRadius:12, marginBottom:8, display:'flex', justifyContent:'space-between', gap:10 }}>
                    <div><div style={{ fontWeight:900, fontSize:13 }}>{displayParcelNumber(p)}</div><div style={{ fontSize:11, color:'#64748b', marginTop:3 }}>Tayari kwa pickup au Agent delivery</div></div>
                    <SBadge status={p.status} />
                  </div>)}
              </>
            )}
          </div>
        )}
      </div>
      {/* ── Receipt window — re-open, print again, SMS again (never a new parcel or charge) ── */}
      {receiptModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '20px 20px 0 0',
            padding: 24, width: '100%', maxWidth: 480, maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: 4 }}>
              <div style={{ fontSize: 15, fontWeight: 900, color: '#1e293b' }}>🧾 Risiti</div>
              <button onClick={() => setReceiptModal(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 20, color: '#64748b' }}>×</button>
            </div>
            <div style={{ fontSize: 12, fontFamily: 'monospace', color: '#1d4ed8', marginBottom: 14 }}>
              {receiptModal.data ? displayParcelNumber(receiptModal.data) : receiptModal.trackingNumber}
              {receiptModal.data && otherParcelNumbers(receiptModal.data, formatWaybill).length > 0
                ? ` · ${otherParcelNumbers(receiptModal.data, formatWaybill).join(' · ')}` : ''}
            </div>

            {receiptModal.loading && (
              <div style={{ textAlign: 'center', padding: 20, color: '#94a3b8' }}>⏳ Inapakia...</div>
            )}
            {receiptModal.error && (
              <div style={{ backgroundColor: '#fef2f2', color: '#b91c1c', borderRadius: 8,
                padding: 12, fontSize: 12, marginBottom: 14 }}>{receiptModal.error}</div>
            )}
            {receiptModal.data?.receipt && (
              <>
                <div style={{ backgroundColor: '#f8fafc', borderRadius: 10, padding: 12, marginBottom: 14 }}>
                  {[
                    ['Namba ya risiti', receiptModal.data.receipt.receiptNumber],
                    ['Mtumaji', [receiptModal.data.receipt.senderName, receiptModal.data.receipt.senderPhone].filter(Boolean).join(' · ')],
                    ['Mpokeaji', [receiptModal.data.receipt.receiverName, receiptModal.data.receipt.receiverPhone].filter(Boolean).join(' · ')],
                    ['Njia', [receiptModal.data.originCity, receiptModal.data.destinationCity].filter(Boolean).join(' → ')],
                    ['Thamani ya mzigo', receiptModal.data.receipt.declaredValue != null ? `TZS ${Number(receiptModal.data.receipt.declaredValue).toLocaleString()}` : null],
                    ['Kiasi kilicholipwa', receiptModal.data.receipt.amountPaid != null ? `TZS ${Number(receiptModal.data.receipt.amountPaid).toLocaleString()}` : null],
                    ['Imelipwa', receiptModal.data.receipt.paidAt ? new Date(receiptModal.data.receipt.paidAt).toLocaleString('sw-TZ') : null],
                  ].filter(([, v]) => v).map(([l, v]) => (
                    <div key={l} style={{ display: 'flex', justifyContent: 'space-between',
                      fontSize: 12, padding: '4px 0' }}>
                      <span style={{ color: '#64748b' }}>{l}</span>
                      <span style={{ fontWeight: 700, color: '#1e293b', textAlign: 'right', marginLeft: 10 }}>{v}</span>
                    </div>
                  ))}
                </div>
                <button onClick={() => handlePrintReceipt(receiptModal.data.receipt)}
                  style={{ width: '100%', backgroundColor: '#1d4ed8', color: '#fff', border: 'none',
                    padding: 12, borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 800,
                    marginBottom: 10 }}>
                  🖨️ Chapisha tena
                </button>
              </>
            )}
            {!receiptModal.loading && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <button onClick={() => handleResendSms('sender', receiptModal.trackingNumber)} disabled={resendLoading !== ''}
                  style={{ flex: 1, backgroundColor: '#eff6ff', color: '#1d4ed8', border: 'none',
                    padding: '9px 10px', borderRadius: 8, cursor: resendLoading ? 'not-allowed' : 'pointer',
                    fontSize: 11.5, fontWeight: 700 }}>
                  {resendLoading === 'sender' ? '⏳...' : '📲 SMS tena — Mtumaji'}
                </button>
                <button onClick={() => handleResendSms('receiver', receiptModal.trackingNumber)} disabled={resendLoading !== ''}
                  style={{ flex: 1, backgroundColor: '#f0fdf4', color: '#16a34a', border: 'none',
                    padding: '9px 10px', borderRadius: 8, cursor: resendLoading ? 'not-allowed' : 'pointer',
                    fontSize: 11.5, fontWeight: 700 }}>
                  {resendLoading === 'receiver' ? '⏳...' : '📲 SMS tena — Mpokeaji'}
                </button>
              </div>
            )}
            {resendMsg && (
              <div style={{ fontSize: 12, marginBottom: 10, color: resendMsg.startsWith('✅') ? '#16a34a' : '#b91c1c' }}>
                {resendMsg}
              </div>
            )}
            {SHOW_CLAIM_CODE_NOTE && <div style={{ fontSize: 11, color: '#64748b' }}>⚠️ {CLAIM_CODE_NOTE}</div>}
          </div>
        </div>
      )}

      {/* ── Parcel history modal — full tracking timeline + all details ────── */}
      {historyModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '20px 20px 0 0',
            padding: 24, width: '100%', maxWidth: 480, maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: 4 }}>
              <div style={{ fontSize: 15, fontWeight: 900, color: '#1e293b' }}>🕘 Historia Kamili</div>
              <button onClick={() => setHistoryModal(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 20, color: '#64748b' }}>×</button>
            </div>
            <div style={{ fontSize: 12, fontFamily: 'monospace', color: '#1d4ed8', marginBottom: 14 }}>
              {historyModal.trackingNumber}
            </div>

            {historyModal.loading && (
              <div style={{ textAlign: 'center', padding: 20, color: '#94a3b8' }}>⏳ Inapakia...</div>
            )}
            {historyModal.error && (
              <div style={{ backgroundColor: '#fef2f2', color: '#b91c1c', borderRadius: 8,
                padding: 12, fontSize: 12 }}>{historyModal.error}</div>
            )}
            {historyModal.data && (
              <>
                <div style={{ backgroundColor: '#f8fafc', borderRadius: 10, padding: 12, marginBottom: 14 }}>
                  {[
                    ['Mtumaji', historyModal.data.senderName],
                    ['Simu ya Mtumaji', historyModal.data.senderPhone],
                    ['Mpokeaji', historyModal.data.recipientName],
                    ['Anwani', historyModal.data.deliveryAddress],
                    ['Bidhaa', historyModal.data.description],
                    ['Njia', [historyModal.data.originCity, historyModal.data.transitCity, historyModal.data.destinationCity]
                      .filter(Boolean).join(' → ')],
                    ['Hub ya Asili', historyModal.data.originAgent],
                    ['Hub ya Kupokea', historyModal.data.destinationAgent],
                    ['Simu ya Hub ya Kupokea', historyModal.data.destinationAgentPhone],
                  ].filter(([, v]) => v).map(([l, v]) => (
                    <div key={l} style={{ display: 'flex', justifyContent: 'space-between',
                      fontSize: 12, padding: '4px 0' }}>
                      <span style={{ color: '#64748b' }}>{l}</span>
                      <span style={{ fontWeight: 700, color: '#1e293b', textAlign: 'right', marginLeft: 10 }}>{v}</span>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                  <button onClick={() => handleResendSms('sender')} disabled={resendLoading !== ''}
                    style={{ flex: 1, backgroundColor: '#eff6ff', color: '#1d4ed8', border: 'none',
                      padding: '9px 10px', borderRadius: 8, cursor: resendLoading ? 'not-allowed' : 'pointer',
                      fontSize: 11.5, fontWeight: 700 }}>
                    {resendLoading === 'sender' ? '⏳...' : '🔄 Tuma SMS ya Mtumaji'}
                  </button>
                  <button onClick={() => handleResendSms('receiver')} disabled={resendLoading !== ''}
                    style={{ flex: 1, backgroundColor: '#f0fdf4', color: '#16a34a', border: 'none',
                      padding: '9px 10px', borderRadius: 8, cursor: resendLoading ? 'not-allowed' : 'pointer',
                      fontSize: 11.5, fontWeight: 700 }}>
                    {resendLoading === 'receiver' ? '⏳...' : '🔄 Tuma SMS ya Mpokeaji'}
                  </button>
                </div>
                {resendMsg && (
                  <div style={{ fontSize: 12, marginBottom: 14, color: resendMsg.startsWith('✅') ? '#16a34a' : '#b91c1c' }}>
                    {resendMsg}
                  </div>
                )}

                <div style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8',
                  marginBottom: 8, textTransform: 'uppercase' }}>Matukio</div>
                {(historyModal.data.history || []).map((h, i) => (
                  <div key={i} style={{ borderLeft: '2px solid #e2e8f0', paddingLeft: 12,
                    marginBottom: 12, position: 'relative' }}>
                    <div style={{ position: 'absolute', left: -5, top: 2, width: 8, height: 8,
                      borderRadius: 4, backgroundColor: '#1d4ed8' }} />
                    <div style={{ fontSize: 12, fontWeight: 800, color: '#1e293b' }}>
                      {STATUS_LABEL[h.status]?.l || h.status}
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>{h.note}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                      {h.updatedBy}{h.handlerPhone ? ` · ${h.handlerPhone}` : ''}
                      {h.handlerLocation ? ` · ${h.handlerLocation}` : ''}
                      {h.createdAt ? ` · ${new Date(h.createdAt).toLocaleString('sw-TZ')}` : ''}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Status update bottom sheet ─────────────────────────────────────── */}
      {statusParcel && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '20px 20px 0 0',
            padding: 24, width: '100%', maxWidth: 480 }}>
            <div style={{ fontSize: 15, fontWeight: 900, color: '#1e293b', marginBottom: 4 }}>
              Sasisha Hali
            </div>
            <div style={{ fontSize: 12, fontFamily: 'monospace', color: '#1d4ed8',
              marginBottom: 14 }}>
              {statusParcel.trackingNumber}
            </div>
            {statusParcel.buyerRequestedDelivery !== true &&
              ['arrived_at_hub', 'awaiting_buyer'].includes(statusParcel.status) &&
              statusParcel.myRole !== 'origin' && (
                <div style={{ marginBottom: 16, padding: 14, borderRadius: 10, backgroundColor: '#eff6ff' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1d4ed8', marginBottom: 8 }}>
                    🚶 Mpokeaji anaweza kuchukua hapa kwa namba ya SMS
                  </div>
                    <div>
                    <button onClick={handleSendPickupCode} disabled={actionLoading}
                      style={{ width: '100%', padding: 12, marginBottom: 8, border: 'none', borderRadius: 8,
                        backgroundColor: '#2563eb', color: '#fff', fontWeight: 800, cursor: 'pointer' }}>
                      Tuma namba kwa mpokeaji
                    </button>
                    <input value={pickupCode} onChange={e => setPickupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      inputMode="numeric" autoComplete="one-time-code" placeholder="Namba ya tarakimu 6"
                      style={{ ...inp, marginBottom: 8 }} />
                    {statusParcel.order?.paymentMethod === 'cod' && (
                      <input value={codBalanceAmount} onChange={e => setCodBalanceAmount(e.target.value)}
                        inputMode="decimal" type="number" min="0" step="0.01"
                        placeholder={`Salio la COD: TZS ${Number(statusParcel.order?.codRemainingBalance || 0).toLocaleString()}`}
                        style={{ ...inp, marginBottom: 8 }} />
                    )}
                    <button onClick={handleConfirmPickup} disabled={actionLoading || pickupCode.length !== 6 ||
                      (statusParcel.order?.paymentMethod === 'cod' && codBalanceAmount.trim() === '')}
                      style={{ width: '100%', padding: 12, border: 'none', borderRadius: 8,
                        backgroundColor: '#16a34a', color: '#fff', fontWeight: 800, cursor: 'pointer' }}>
                      {actionLoading ? '⏳' : '✅ Nimekabidhi kwa mpokeaji'}
                    </button>
                    </div>
                </div>
              )}
            {statusParcel.buyerRequestedDelivery === true && statusParcel.localAgentId &&
              ['arrived_at_hub', 'awaiting_buyer'].includes(statusParcel.status) &&
              statusParcel.myRole !== 'origin' && (
                <div style={{ marginBottom: 16, padding: 14, borderRadius: 10, backgroundColor: '#eff6ff' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
                    Kabidhi kifurushi kwa wakala aliyechaguliwa
                  </div>
                  <button onClick={handleIssueAgentHandoff} disabled={actionLoading}
                    style={{ width: '100%', padding: 12, border: 'none', borderRadius: 8,
                      backgroundColor: '#2563eb', color: '#fff', fontWeight: 800, cursor: 'pointer' }}>
                    Toa namba ya makabidhiano
                  </button>
                  {agentHandoffChallenge?.trackingNumber === statusParcel.trackingNumber && (
                    <div style={{ marginTop: 10, fontSize: 13 }}>
                      Mwonyeshe wakala namba hii akiwa hapa. Inaisha baada ya dakika 10.
                      <div style={{ fontSize: 26, fontWeight: 900, letterSpacing: 6 }}>
                        {agentHandoffChallenge.code}
                      </div>
                    </div>
                  )}
                </div>
              )}
            <select value={newStatus} onChange={e => setNewStatus(e.target.value)}
              style={{ ...inp, marginBottom: 12 }}>
              <option value="">— Chagua Hali Mpya —</option>
              {(() => {
                // Stage-based gating, mirroring the backend's PRE_DISPATCH_
                // STATUSES guard in updateParcelStatus() — for a single-hub
                // parcel (myRole: 'both'), the role filters below never
                // narrow anything down, so without this an agent could see
                // (and mistakenly pick) "Imefika Hubuni" while the parcel
                // hasn't even been dispatched yet, or re-pick an intake step
                // after it already has been. This only hides options the
                // backend would reject anyway — it's a UX guard, not the
                // safety net.
                const preDispatch = [
                  'pending', 'collection_requested', 'collected_by_agent',
                  'received_at_hub', 'verified', 'ready_for_dispatch',
                ].includes(statusParcel.status);
                return [
                  // Origin-side statuses — only offered when this hub is the
                  // sender (or both), matching the backend's ownership check.
                  ...(statusParcel.myRole !== 'destination' ? [
                    // Intake steps only make sense before the parcel has
                    // ever been dispatched — hidden afterward.
                    ...(preDispatch ? [
                      ['received_at_hub',   '📥 Imepokewa Hubuni'],
                      ['verified',         '✅ Imethibitishwa'],
                      ['ready_for_dispatch','📦 Tayari Kutuma'],
                    ] : []),
                    ['in_transit',       '🚚 Njiani'],
                  ] : []),
                  // Destination-side statuses — only offered when this hub is
                  // the receiver (or both). Marking "arrived" is
                  // the receiving hub's job, never the sender's — and never
                  // possible before the parcel has actually been dispatched.
                  ...(statusParcel.myRole !== 'origin' && !preDispatch ? [
                    ['arrived_at_hub',   '🏢 Imefika Hubuni'],
                    ['awaiting_buyer',   '⏳ Inasubiri Mteja'],
                  ] : []),
                ].map(([v, l]) => <option key={v} value={v}>{l}</option>);
              })()}
            </select>
            <input type="text" placeholder="Maelezo (hiari)"
              value={statusNote} onChange={e => setStatusNote(e.target.value)}
              style={{ ...inp, marginBottom: 14 }} />
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handleStatus} disabled={actionLoading || !newStatus}
                style={{ flex: 2, backgroundColor: !newStatus ? '#94a3b8' : '#1d4ed8',
                  color: '#fff', border: 'none', padding: 14, borderRadius: 10,
                  cursor: 'pointer', fontSize: 14, fontWeight: 900 }}>
                {actionLoading ? '⏳' : '💾 Hifadhi'}
              </button>
              <button onClick={() => { setStatusParcel(null); setNewStatus(''); setStatusNote(''); setPickupCode(''); setCodBalanceAmount(''); }}
                style={{ flex: 1, background: '#fff', color: '#64748b',
                  border: '2px solid #e2e8f0', padding: 14, borderRadius: 10,
                  cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                Ghairi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Assign Transport Modal ──────────────────────────────────── */}
      {assignTransport && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
          zIndex: 2000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '20px 20px 0 0',
            padding: 20, width: '100%', maxWidth: 560, maxHeight: '85vh',
            overflowY: 'auto', boxShadow: '0 -8px 32px rgba(0,0,0,0.2)' }}>

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 900, color: '#1e293b' }}>
                  🚌 Panga Usafiri
                </div>
                <div style={{ fontSize: 12, color: '#64748b' }}>
                  {assignTransport.fromCity} → {assignTransport.toCity}
                  {assignTransport.trackingNumber && ` · ${assignTransport.trackingNumber}`}
                </div>
              </div>
              <button onClick={() => setAssignTransport(null)}
                style={{ background: 'none', border: 'none', fontSize: 24,
                  cursor: 'pointer', color: '#64748b' }}>×</button>
            </div>

            {loadingTransport ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
                🔍 Inatafuta wasafirishaji...
              </div>
            ) : (<>

              {/* Published availability — today/tomorrow */}
              {transportOptions?.published?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: '#16a34a',
                    marginBottom: 8 }}>✅ NAFASI ZILIZOCHAPISHWA LEO / KESHO</div>
                  {transportOptions.published.map(avail => (
                    <div key={avail.id}
                      onClick={() => { setSelectedProvider(avail.provider); setSelectedAvailId(avail.id); }}
                      style={{ padding: 12, borderRadius: 12, marginBottom: 8, cursor: 'pointer',
                        border: `2px solid ${selectedAvailId === avail.id ? '#16a34a' : '#e2e8f0'}`,
                        backgroundColor: selectedAvailId === avail.id ? '#f0fdf4' : '#fff' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b' }}>
                            {avail.provider?.name}
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>
                            📅 {avail.date} {avail.departureTime && `· ${avail.departureTime}`}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#16a34a' }}>
                            {avail.totalSlots - avail.usedSlots} nafasi
                          </div>
                          {avail.notes && (
                            <div style={{ fontSize: 10, color: '#94a3b8' }}>{avail.notes}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* All verified providers on this route */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#1d4ed8', marginBottom: 8 }}>
                  🚌 WASAFIRISHAJI KWENYE NJIA HII
                </div>
                {transportOptions?.providers?.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 20, color: '#94a3b8',
                    backgroundColor: '#f8fafc', borderRadius: 10 }}>
                    Hakuna msafirishaji aliyesajiliwa kwenye njia hii bado.
                    <br />
                    <span style={{ fontSize: 11 }}>Unaweza bado kumpa mgawo mtu yeyote hapa chini.</span>
                  </div>
                ) : transportOptions?.providers?.map(p => (
                  <div key={p.id}
                    onClick={() => { setSelectedProvider(p); setSelectedAvailId(null); }}
                    style={{ padding: 12, borderRadius: 12, marginBottom: 8, cursor: 'pointer',
                      border: `2px solid ${selectedProvider?.id === p.id && !selectedAvailId ? '#1d4ed8' : '#e2e8f0'}`,
                      backgroundColor: selectedProvider?.id === p.id && !selectedAvailId ? '#eff6ff' : '#fff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b' }}>
                          {p.type === 'bus' ? '🚌' : p.type === 'van' ? '🚐' : p.type === 'courier' ? '📦' : '🚛'} {p.name}
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>
                          {p.contactPhone}
                          {p.rating > 0 && ` · ⭐ ${p.rating}`}
                          {p.completedAssignments > 0 && ` · ${p.completedAssignments} zilizokamilika`}
                        </div>
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>
                        {p.confirmMode === 'auto' ? '⚡ Auto' : '✋ Manual'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Selected provider details + confirm */}
              {selectedProvider && (
                <div style={{ backgroundColor: '#f8fafc', borderRadius: 12,
                  padding: 14, marginBottom: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: '#1e293b', marginBottom: 10 }}>
                    ✅ Umechagua: {selectedProvider.name}
                    {selectedAvailId && ' (nafasi iliyochapishwa)'}
                  </div>
                  <div style={{ marginBottom: 10 }}>
                    <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b',
                      display: 'block', marginBottom: 4 }}>
                      Bei Iliyokubaliwa (TZS) — hiari
                    </label>
                    <input type="number"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: 8,
                        border: '1px solid #e2e8f0', fontSize: 13, boxSizing: 'border-box' }}
                      placeholder="Bei mnayokubaliwa nje ya KenteXa"
                      value={agreedPrice}
                      onChange={e => setAgreedPrice(e.target.value)} />
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b',
                      display: 'block', marginBottom: 4 }}>
                      Maelezo / Maagizo
                    </label>
                    <input
                      style={{ width: '100%', padding: '8px 12px', borderRadius: 8,
                        border: '1px solid #e2e8f0', fontSize: 13, boxSizing: 'border-box' }}
                      placeholder="e.g. Parcel nyepesi, iangaliwe vizuri"
                      value={transportNotes}
                      onChange={e => setTransportNotes(e.target.value)} />
                  </div>
                  <button onClick={handleAssignTransport} disabled={assigningTransport}
                    style={{ width: '100%', background: 'linear-gradient(135deg,#1d4ed8,#7c3aed)',
                      color: '#fff', border: 'none', borderRadius: 10, padding: '12px 0',
                      fontSize: 14, fontWeight: 800,
                      cursor: assigningTransport ? 'not-allowed' : 'pointer' }}>
                    {assigningTransport ? '⏳ Inatuma...' :
                      selectedProvider.confirmMode === 'auto'
                        ? `⚡ Panga Moja kwa Moja — ${selectedProvider.name}`
                        : `📲 Tuma Ombi — ${selectedProvider.name}`}
                  </button>
                  {selectedProvider.confirmMode === 'manual' && (
                    <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', marginTop: 6 }}>
                      Msafirishaji atapata arifa na atathibitisha
                    </div>
                  )}
                </div>
              )}
            </>)}
          </div>
        </div>
      )}

      {transferModal && (
        <div style={{ position:'fixed',inset:0,backgroundColor:'rgba(0,0,0,0.5)',zIndex:3000,display:'flex',alignItems:'flex-end' }}>
          <div style={{ width:'100%',backgroundColor:'#fff',borderRadius:'20px 20px 0 0',padding:'24px 20px 40px', maxHeight:'85vh', overflowY:'auto' }}>
            <div style={{ fontSize:16,fontWeight:900,color:'#1e293b',marginBottom:4 }}>🏢 Hamisha kwa Super Agent Mwingine</div>
            <div style={{ fontSize:12,color:'#64748b',marginBottom:16 }}>{transferModal.trackingNumber}</div>
            <div style={{ fontSize:11,color:'#94a3b8',marginBottom:14, lineHeight:1.5 }}>
              Mshirika wako atapata ujumbe kuhusu kifurushi kinachokuja, na mnunuzi atapata jina/simu/mahali pake pa kuchukulia.
            </div>

            <div style={{ marginBottom: 10 }}>
              <IntelligentLocationInput
                label="" value={transferDestText} onTextChange={setTransferDestText}
                onResolved={loc => {
                  if (!loc) return;
                  const cityStr = loc.regionName || loc.districtName || '';
                  setTransferForm(f => ({ ...f, destinationCity: cityStr, destinationSuperAgentId: null }));
                  fetchTransferHubs(cityStr);
                }} placeholder="Tafuta eneo la Super Agent…" required
              />
            </div>

            {transferHubsLoading ? (
              <div style={{ fontSize:12,color:'#94a3b8',padding:'8px 0' }}>⏳ Inatafuta...</div>
            ) : transferHubs.length > 0 && (
              <>
                <div style={{ fontSize:11,fontWeight:700,color:'#7c3aed',marginBottom:8 }}>
                  Super Agent aliyesajiliwa
                </div>
                {transferHubs.map(h => (
                  <label key={h.id} onClick={() => setTransferForm(f => ({ ...f, destinationSuperAgentId: h.id }))}
                    style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderRadius:10,
                      marginBottom:6, cursor:'pointer',
                      border:`2px solid ${transferForm.destinationSuperAgentId === h.id ? '#7c3aed' : '#e2e8f0'}`,
                      backgroundColor: transferForm.destinationSuperAgentId === h.id ? '#f5f3ff' : '#f8fafc' }}>
                    <input type="radio" checked={transferForm.destinationSuperAgentId === h.id} readOnly />
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13,fontWeight:800,color:'#1e293b' }}>{h.businessName}</div>
                      <div style={{ fontSize:11,color:'#64748b' }}>📍 {h.address || h.city}</div>
                    </div>
                  </label>
                ))}
              </>
            )}

            {!transferForm.destinationSuperAgentId && (
              <>
                <div style={{ fontSize:11,fontWeight:700,color:'#94a3b8',marginTop:10,marginBottom:8 }}>
                  AU JAZA MAWASILIANO YA MSHIRIKA (siyo Super Agent aliyesajiliwa)
                </div>
                <input style={{ width:'100%',padding:'10px 12px',borderRadius:10,border:'1px solid #e2e8f0',fontSize:14,marginBottom:10,boxSizing:'border-box',outline:'none' }}
                  placeholder="Jina la mshirika/biashara"
                  value={transferForm.manualContactName}
                  onChange={e => setTransferForm(f => ({ ...f, manualContactName: e.target.value }))} />
                <input style={{ width:'100%',padding:'10px 12px',borderRadius:10,border:'1px solid #e2e8f0',fontSize:14,marginBottom:10,boxSizing:'border-box',outline:'none' }}
                  placeholder="Namba ya simu"
                  value={transferForm.manualContactPhone}
                  onChange={e => setTransferForm(f => ({ ...f, manualContactPhone: e.target.value }))} />
                <input style={{ width:'100%',padding:'10px 12px',borderRadius:10,border:'1px solid #e2e8f0',fontSize:14,marginBottom:10,boxSizing:'border-box',outline:'none' }}
                  placeholder="Mahali/Ofisi (e.g. China Plaza, Ofisi 23, Kariakoo)"
                  value={transferForm.manualContactAddress}
                  onChange={e => setTransferForm(f => ({ ...f, manualContactAddress: e.target.value }))} />
              </>
            )}

            <input style={{ width:'100%',padding:'10px 12px',borderRadius:10,border:'1px solid #e2e8f0',fontSize:14,marginTop:4,marginBottom:10,boxSizing:'border-box',outline:'none' }}
              placeholder="Kampuni ya usafiri (hiari — e.g. Shabiby Bus)"
              value={transferForm.transportCompany}
              onChange={e => setTransferForm(f => ({ ...f, transportCompany: e.target.value }))} />
            <input style={{ width:'100%',padding:'10px 12px',borderRadius:10,border:'1px solid #e2e8f0',fontSize:14,marginBottom:16,boxSizing:'border-box',outline:'none' }}
              placeholder="Maelezo (hiari)"
              value={transferForm.note}
              onChange={e => setTransferForm(f => ({ ...f, note: e.target.value }))} />
            <div style={{ display:'flex',gap:10 }}>
              <button onClick={() => setTransferModal(null)}
                style={{ flex:1,backgroundColor:'#f1f5f9',color:'#64748b',border:'none',borderRadius:10,padding:'12px 0',cursor:'pointer',fontSize:14,fontWeight:700 }}>
                Funga
              </button>
              <button onClick={() => handleTransferHub(transferModal.trackingNumber, transferForm)}
                disabled={!transferForm.destinationSuperAgentId && !transferForm.manualContactName}
                style={{ flex:2,background: (!transferForm.destinationSuperAgentId && !transferForm.manualContactName) ? '#c4b5fd' : 'linear-gradient(135deg,#7c3aed,#1d4ed8)',color:'#fff',border:'none',borderRadius:10,padding:'12px 0',cursor:'pointer',fontSize:14,fontWeight:800 }}>
                ✅ Hamisha Sasa
              </button>
            </div>
          </div>
        </div>
      )}

      {showVerifyIdentity && (
        <VerifyIdentityModal
          onClose={() => setShowVerifyIdentity(false)}
          onVerified={() => {
            setShowVerifyIdentity(false);
            api.get('/identity/me').then(r => setIdentityStatus(r.data)).catch(() => {});
          }}
        />
      )}
    </div>
  );
};

export default SuperAgentDashboard;
